import React, { useState, useEffect } from 'react';
import './App.css';
import config from './config';
import AuthScreen from './AuthScreen';
import LandingPage from './LandingPage';
import HotspotQuestion from './HotspotQuestion';
import ExamInviteModal from './ExamInviteModal';
import VoteWidget from './VoteWidget';
import { authGetCurrentUser, authGetToken, authSignOut, authBootstrapFromUrlToken, extractEmailFromUrlToken, isAuthConfigured } from './auth';
import { useTier } from './useTier';
import UpgradeBlock from './UpgradeBlock';
import LearningPlatformPromo from './LearningPlatformPromo';
import BillingSuccess from './BillingSuccess';
import { hasAccessToCert, tierForCert } from './certTiers';

const API_URL = config.apiUrl;

// Exam-only $14.99/mo per-cert Stripe link — must match UpgradeBlock.EXAM_STRIPE_BASE.
const EXAM_STRIPE_BASE = 'https://buy.stripe.com/3cI6oGbkn60YekxeOH5gc0g';

/** Build the $14.99/mo Stripe URL with both the user's Cognito sub AND the
 *  cert code encoded in client_reference_id ('<sub>__<CERT-CODE>'), so the
 *  webhook knows which cert was paid for. Fallback omits cert code (legacy
 *  bundle path) when a cert isn't supplied. */
async function buildExamStripeUrl(certCode) {
  try {
    const { fetchAuthSession } = await import('aws-amplify/auth');
    const session = await fetchAuthSession().catch(() => null);
    const sub = session?.tokens?.idToken?.payload?.sub || '';
    if (!sub) return EXAM_STRIPE_BASE;
    const sep = EXAM_STRIPE_BASE.includes('?') ? '&' : '?';
    const ref = certCode ? `${sub}__${String(certCode).toUpperCase()}` : sub;
    return `${EXAM_STRIPE_BASE}${sep}client_reference_id=${encodeURIComponent(ref)}`;
  } catch {
    return EXAM_STRIPE_BASE;
  }
}

/** Open the $14.99/mo subscribe flow in a new tab for the given cert.
 *  Used by the legacy fallback paths (fetchQuestions 402, enforceCertTier,
 *  enforceAttemptLimit) where a confirm dialog is the right UX. The cert
 *  card render path uses the inline UpgradeBlock pill instead. */
async function openExamSubscribe(certCode) {
  const url = await buildExamStripeUrl(certCode);
  window.open(url, '_blank', 'noopener,noreferrer');
}

/** Classify a cert into a visual "family" so we can color-stripe the
 *  card. Color choice and stripe styling live in App.css under
 *  .cert-card.fam-* selectors. */
function certFamily(certCode) {
  const c = String(certCode || '').toUpperCase();
  if (c.startsWith('AIF') || c.startsWith('AIP') || c.startsWith('MLA')) return 'ai';
  if (c.startsWith('SAA') || c.startsWith('SAP')) return 'architect';
  if (c.startsWith('DVA')) return 'developer';
  if (c.startsWith('DOP')) return 'devops';
  if (c.startsWith('SOA')) return 'cloudops';
  if (c.startsWith('DEA')) return 'data';
  if (c.startsWith('ANS')) return 'networking';
  if (c.startsWith('SCS')) return 'security';
  if (c.startsWith('CLF')) return 'foundational';
  return 'foundational';
}

/** Map a cert's required tier to a friendly label shown as a pill on the
 *  card (e.g., 'Student', 'Pro'). 'free' renders no pill. */
function certTierBadge(certCode) {
  const t = tierForCert(certCode);
  if (t === 'student') return { label: 'Student', cls: 'tier-badge-student' };
  if (t === 'pro') return { label: 'Pro', cls: 'tier-badge-pro' };
  return null;
}

// Helper function to detect how many answers are required.
// Handles "Choose/Select/Pick N", digit forms, and the common
// "(Chose three.)" data typo (missing the second "o").
// How many options the learner must select. An explicit requiredCount on the question
// wins over parsing the stem: a few items lost their "(Choose two.)" marker in the
// source data, and reading the stem alone made the UI ask for one answer while the
// answer key held two, so the question could never be marked correct.
const getRequiredAnswerCount = (questionOrText) => {
  const isObject = questionOrText && typeof questionOrText === 'object';
  if (isObject && Number.isInteger(questionOrText.requiredCount) && questionOrText.requiredCount > 0) {
    return questionOrText.requiredCount;
  }
  const questionText = isObject ? questionOrText.question : questionOrText;
  if (!questionText) return 1;
  const lowerText = questionText.toLowerCase();
  const match = lowerText.match(/\b(?:choose|chose|select|pick)\s+(two|three|four|five|2|3|4|5)\b/);
  if (!match) return 1;
  const map = { two: 2, three: 3, four: 4, five: 5, '2': 2, '3': 3, '4': 4, '5': 5 };
  return map[match[1]] || 1;
};

// Admin emails — these users get extra controls
const ADMIN_EMAILS = ['jossai@dreamandbuildllc.com'];

// AWS Certification data
const AWS_CERTIFICATIONS = [
  { id: 'clf-c02', name: 'AWS Certified Cloud Practitioner', code: 'CLF-C02', level: 'Foundational', available: true, examQuestions: 65, examMinutes: 90 },
  { id: 'aif-c01', name: 'AWS Certified AI Practitioner', code: 'AIF-C01', level: 'Foundational', available: true, examQuestions: 65, examMinutes: 120 },
  { id: 'aip-c01', name: 'AWS Certified Generative AI Developer – Professional', code: 'AIP-C01', level: 'Professional', available: true, examQuestions: 65, examMinutes: 120 },
  { id: 'saa-c03', name: 'AWS Certified Solutions Architect – Associate', code: 'SAA-C03', level: 'Associate', available: true, examQuestions: 65, examMinutes: 130 },
  { id: 'dva-c02', name: 'AWS Certified Developer – Associate', code: 'DVA-C02', level: 'Associate', available: true, examQuestions: 65, examMinutes: 130 },
  { id: 'soa-c03', name: 'AWS Certified CloudOps Engineer – Associate', code: 'SOA-C03', level: 'Associate', available: true, examQuestions: 65, examMinutes: 130 },
  { id: 'soa-c02', name: 'AWS Certified SysOps Administrator – Associate (SOA-C02)', code: 'SOA-C02', level: 'Associate', available: false, note: 'Retired Sep 2025 — replaced by SOA-C03', examQuestions: 65, examMinutes: 130 },
  { id: 'dea-c01', name: 'AWS Certified Data Engineer – Associate', code: 'DEA-C01', level: 'Associate', available: true, examQuestions: 65, examMinutes: 130 },
  { id: 'mla-c01', name: 'AWS Certified Machine Learning Engineer – Associate', code: 'MLA-C01', level: 'Associate', available: true, examQuestions: 65, examMinutes: 130 },
  { id: 'sap-c02', name: 'AWS Certified Solutions Architect – Professional', code: 'SAP-C02', level: 'Professional', available: true, examQuestions: 75, examMinutes: 180 },
  { id: 'dop-c02', name: 'AWS Certified DevOps Engineer – Professional', code: 'DOP-C02', level: 'Professional', available: true, examQuestions: 75, examMinutes: 180 },
  { id: 'ans-c01', name: 'AWS Certified Advanced Networking – Specialty', code: 'ANS-C01', level: 'Specialty', available: true, examQuestions: 65, examMinutes: 170 },
  { id: 'scs-c02', name: 'AWS Certified Security – Specialty (SCS-C02)', code: 'SCS-C02', level: 'Specialty', available: true, note: 'Retired Dec 2025 — replaced by SCS-C03', examQuestions: 65, examMinutes: 170 },
  { id: 'scs-c03', name: 'AWS Certified Security – Specialty', code: 'SCS-C03', level: 'Specialty', available: false, note: 'Coming soon', examQuestions: 65, examMinutes: 170 },
];

// Domain short names for display
const DOMAIN_SHORT_NAMES = {
  'Network Design': 'Design',
  'Network Implementation': 'Implementation',
  'Network Management and Operations': 'Mgmt & Ops',
  'Network Security': 'Security',
  'Fundamentals of AI and ML': 'AI/ML Fundamentals',
  'Fundamentals of Generative AI': 'Generative AI',
  'Applications of Foundation Models': 'FM Applications',
  'Guidelines for Responsible AI': 'Responsible AI'
};

// History Item Component with domain stats
function HistoryItem({ quiz, wrongCount, onLoad, onDelete, onRetryWrong, fetchDomainStats }) {
  const [expanded, setExpanded] = useState(false);
  const [domainStats, setDomainStats] = useState(null);
  const [loadingStats, setLoadingStats] = useState(false);

  const handleExpand = async () => {
    if (!expanded && !domainStats && quiz.completed) {
      setLoadingStats(true);
      const stats = await fetchDomainStats(quiz);
      setDomainStats(stats);
      setLoadingStats(false);
    }
    setExpanded(!expanded);
  };

  return (
    <div className={`history-item ${expanded ? 'expanded' : ''}`}>
      <div className="history-header" onClick={handleExpand}>
        <div className="history-info">
          <span className={`history-status ${quiz.completed ? 'completed' : 'in-progress'}`}>
            {quiz.completed ? '✓ Completed' : '⏸ In Progress'}
          </span>
          <span className="history-mode">
            {quiz.mode === 'quiz' ? 'Quiz Mode' : 
             quiz.mode === 'browse' ? 'Browse Mode' : 
             quiz.mode === 'range' ? 'Range Mode' : 'Quiz'}
          </span>
        </div>
        <div className="history-date">
          {new Date(quiz.startedAt).toLocaleString()}
        </div>
        <span className={`expand-icon ${expanded ? 'expanded' : ''}`}>▼</span>
      </div>
      
      <div className="history-stats">
        <span>Score: {quiz.score.correct}/{quiz.score.total}</span>
        <span>Progress: {quiz.currentQuestionIndex + 1}/{quiz.questions.length}</span>
        {quiz.score.total > 0 && (
          <span>Accuracy: {Math.round((quiz.score.correct / quiz.score.total) * 100)}%</span>
        )}
        {wrongCount > 0 && (
          <span className="wrong-count">❌ {wrongCount} wrong</span>
        )}
      </div>

      {expanded && (
        <div className="history-expanded">
          {quiz.completed && (
            <div className="domain-breakdown">
              <h4>Performance by Domain</h4>
              {loadingStats ? (
                <div className="loading-stats">Loading domain stats...</div>
              ) : domainStats ? (
                <div className="domain-stats-grid">
                  {Object.entries(domainStats).map(([domain, stats]) => {
                    const percentage = stats.total > 0 
                      ? Math.round((stats.correct / stats.total) * 100) 
                      : null;
                    return (
                      <div key={domain} className="domain-stat">
                        <span className="domain-name">{DOMAIN_SHORT_NAMES[domain] || domain}</span>
                        <div className="domain-bar-container">
                          <div 
                            className={`domain-bar ${percentage !== null ? (percentage >= 70 ? 'good' : percentage >= 50 ? 'medium' : 'poor') : 'empty'}`}
                            style={{ width: percentage !== null ? `${percentage}%` : '0%' }}
                          />
                        </div>
                        <span className="domain-score">
                          {stats.total > 0 ? `${stats.correct}/${stats.total} (${percentage}%)` : 'N/A'}
                        </span>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="no-stats">Unable to load domain stats</div>
              )}
            </div>
          )}
        </div>
      )}

      <div className="history-actions">
        <button 
          className="resume-btn"
          onClick={() => onLoad(quiz)}
        >
          {quiz.completed ? 'Review' : 'Resume'}
        </button>
        {quiz.completed && wrongCount > 0 && (
          <button 
            className="retry-wrong-btn"
            onClick={() => onRetryWrong(quiz)}
          >
            🔄 Retry Wrong ({wrongCount})
          </button>
        )}
        <button 
          className="delete-btn"
          onClick={() => {
            if (window.confirm('Delete this quiz from history?')) {
              onDelete(quiz.quizId);
            }
          }}
        >
          Delete
        </button>
      </div>
    </div>
  );
}

function App() {
  const [questions, setQuestions] = useState([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [selectedAnswers, setSelectedAnswers] = useState([]);
  const [showResult, setShowResult] = useState(false);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [score, setScore] = useState({ correct: 0, total: 0 });
  const [viewMode, setViewMode] = useState('quiz');
  const [questionStats, setQuestionStats] = useState(null);
  const [rangeStart, setRangeStart] = useState('');
  const [rangeEnd, setRangeEnd] = useState('');
  const [quizHistory, setQuizHistory] = useState([]);
  const [currentQuizId, setCurrentQuizId] = useState(null);
  const [answeredQuestions, setAnsweredQuestions] = useState({});
  const [quizStarted, setQuizStarted] = useState(false);
  const [sidePanelOpen, setSidePanelOpen] = useState(false);
  const [showJumpDialog, setShowJumpDialog] = useState(false);
  const [jumpToNumber, setJumpToNumber] = useState('');
  const [selectedCertification, setSelectedCertification] = useState(null);
  const [domainStats, setDomainStats] = useState(null);
  const [user, setUser] = useState(null);
  const [authChecked, setAuthChecked] = useState(!isAuthConfigured());
  // Public marketing landing gate: unauthenticated visitors see LandingPage
  // first (marketing + anti-phishing legitimacy) and only reach AuthScreen
  // after clicking a sign in / get started CTA. Auto-skip the landing when
  // the user arrives via an SSO/exam deep link so we don't interrupt them.
  const [showAuth, setShowAuth] = useState(false);
  // Which AuthScreen tab to open when leaving the landing page: 'signUp'
  // for "Get started / Create free account" CTAs, 'signIn' for "Sign in".
  const [authInitialMode, setAuthInitialMode] = useState('signIn');
  // SSO prefill — when the user arrives with a Learning Platform JWT in
  // ?token=, we can't bootstrap their session (different Cognito pool),
  // but we CAN read their email out of the unverified JWT to pre-fill
  // the AuthScreen email field so the friction is just the password.
  const [prefilledAuthEmail, setPrefilledAuthEmail] = useState(null);
  const [hiddenCerts, setHiddenCerts] = useState([]);
  const [copiedCertId, setCopiedCertId] = useState(null);
  const [inviteModalCert, setInviteModalCert] = useState(null); // cert object for the invite modal
  // Preview modal — free 10-question sample of any cert (no subscription needed).
  // Shown when a free user clicks 'Preview 10 free questions' on a locked card.
  const [previewCert, setPreviewCert] = useState(null);     // cert object being previewed
  const [previewQuestions, setPreviewQuestions] = useState([]);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState(null);
  const [previewIndex, setPreviewIndex] = useState(0);
  const [previewRevealed, setPreviewRevealed] = useState(false);

  /** Fetch 10 free preview questions from the public endpoint and open the modal. */
  const openPreviewModal = async (cert) => {
    if (!cert?.id) return;
    setPreviewCert(cert);
    setPreviewQuestions([]);
    setPreviewIndex(0);
    setPreviewRevealed(false);
    setPreviewError(null);
    setPreviewLoading(true);
    try {
      const r = await fetch(`${API_URL}/api/preview-questions?cert=${encodeURIComponent(cert.id)}&count=10`);
      if (!r.ok) throw new Error(`Server returned ${r.status}`);
      const data = await r.json();
      if (!Array.isArray(data) || data.length === 0) {
        setPreviewError('No preview questions available for this cert yet.');
      } else {
        setPreviewQuestions(data);
      }
    } catch (err) {
      setPreviewError('Could not load preview questions. Please try again.');
    } finally {
      setPreviewLoading(false);
    }
  };

  const closePreviewModal = () => {
    setPreviewCert(null);
    setPreviewQuestions([]);
    setPreviewIndex(0);
    setPreviewRevealed(false);
    setPreviewError(null);
  };
  const [adminView, setAdminView] = useState(null); // null | 'scores' | 'users'
  const [allScores, setAllScores] = useState([]);
  const [adminScoreDetail, setAdminScoreDetail] = useState(null); // full quiz detail for drill-down
  const [adminScoreFilter, setAdminScoreFilter] = useState('all'); // 'all' | 'exam' | 'quiz'
  const [settingsPanelOpen, setSettingsPanelOpen] = useState(false);
  const [quizDownloadCount, setQuizDownloadCount] = useState(15);

  // Admin user-management state
  const [allUsers, setAllUsers] = useState([]);            // [{ userId, email, status, blockedCerts, ... }]
  const [usersLoading, setUsersLoading] = useState(false);
  const [userSearch, setUserSearch] = useState('');
  const [savingUserId, setSavingUserId] = useState(null);  // user currently being saved (for spinner)

  // Per-user cert-access for the SIGNED-IN user (used to gate cert cards).
  // Empty list = full access. Admins are not gated.
  const [myBlockedCerts, setMyBlockedCerts] = useState([]);

  // Notify the parent frame (Dream and Build Cloud) to hide/show its chat
  // widget depending on whether we're on the cert selection screen or inside
  // an active quiz. The parent listens for { type: 'EXAM_APP_HIDE_CHAT' } and
  // { type: 'EXAM_APP_SHOW_CHAT' }.
  useEffect(() => {
    if (window.parent && window.parent !== window) {
      const type = !selectedCertification
        ? 'EXAM_APP_HIDE_CHAT'
        : 'EXAM_APP_SHOW_CHAT';
      window.parent.postMessage({ type }, '*');
    }
  }, [selectedCertification]);

  // Exam timer state (only active in exam mode via direct link)
  const [examTimeLeft, setExamTimeLeft] = useState(null);  // seconds remaining
  const [examTimerPaused, setExamTimerPaused] = useState(false);
  const [examTimerExpired, setExamTimerExpired] = useState(false);

  // Exam lock state
  const [showExamDisclaimer, setShowExamDisclaimer] = useState(false);
  const [showQuizDisclaimer, setShowQuizDisclaimer] = useState(false); // quiz link ready screen
  const [examActive, setExamActive] = useState(false); // true once exam has started
  const [examLeftWarning, setExamLeftWarning] = useState(false); // user tried to leave
  const [isExamLink, setIsExamLink] = useState(false); // true when entering via cert card or direct link
  // Tier from skills-platform billing API. Owner bypass is built in.
  const {
    tier: userTier,
    email: userTierEmail,
    source: tierSource,
    stripeCustomerId: examStripeCustomerId,
    ownedCerts: userOwnedCerts,
    legacyAllAccess: userLegacyAllAccess,
    betaMode: userBetaMode,
  } = useTier();
  // Bundle the per-cert access opts so we don't repeat them at every
  // hasAccessToCert callsite. UpgradeBlock callsites also use these.
  //
  // betaMode rides along here on purpose: hasAccessToCert() returns true for
  // every cert while beta is on, which makes every UpgradeBlock and Subscribe
  // pill on the page disappear without touching their individual callsites.
  const accessOpts = {
    ownedCerts: userOwnedCerts,
    legacyAllAccess: userLegacyAllAccess,
    betaMode: userBetaMode,
  };
  const hasExamSub = Boolean(examStripeCustomerId);

  // Billing success route — Stripe redirects post-checkout to
  // /billing/success?session_id=...  We capture that here once at mount,
  // render the BillingSuccess splash instead of the cert-selection screen,
  // and clear the flag (+ replaceState the URL) when the user clicks
  // 'Start practicing'.
  const [showBillingSuccess, setShowBillingSuccess] = useState(() => {
    try {
      return typeof window !== 'undefined' && window.location.pathname === '/billing/success';
    } catch {
      return false;
    }
  });
  const dismissBillingSuccess = () => {
    setShowBillingSuccess(false);
    try {
      window.history.replaceState({}, '', '/');
    } catch { /* ignore */ }
  };

  // Open the Stripe billing portal so the user can manage / cancel any of
  // their per-cert $14.99 exam subscriptions. The lambda creates a one-time
  // portal session and returns a URL we navigate to in a new tab.
  const handleOpenExamPortal = async () => {
    try {
      const jwt = await authGetToken();
      if (!jwt) {
        window.alert('Please sign in to manage your subscription.');
        return;
      }
      const r = await fetch(`${API_URL}/billing/portal`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${jwt}` },
        body: JSON.stringify({ returnUrl: window.location.href }),
      });
      if (!r.ok) {
        const data = await r.json().catch(() => ({}));
        window.alert(data?.message || 'Could not open the billing portal. Try again or contact support.');
        return;
      }
      const { url } = await r.json();
      if (url) window.open(url, '_blank', 'noopener,noreferrer');
    } catch (err) {
      console.error('[billing] portal failed:', err);
      window.alert('Could not open the billing portal. Try again or contact support.');
    }
  };

  // ── Free-tier attempt tracking ──────────────────────────────────────────
  // Free users get 1 quiz attempt + 1 exam attempt per cert, lifetime.
  // Stored in localStorage keyed by userId so different users don't share.
  // Only enforced for free-tier certs (CLF-C02 today).
  // Cloud Practitioner (CLF-C02) is the free cert and is now UNLIMITED for
  // free-tier users — no per-attempt cap on its quizzes or exams. Leaving
  // this set empty means enforceAttemptLimit never blocks (every free cert
  // is unlimited). Paid certs are gated by subscription, not attempt count.
  const FREE_TIER_CERTS = new Set([]);

  const getAttemptKey = () => {
    const uid = user?.username || user?.userId || 'anon';
    return `sp_exam_attempts_${uid}`;
  };

  const loadAttempts = () => {
    try { return JSON.parse(localStorage.getItem(getAttemptKey()) || '{}'); }
    catch { return {}; }
  };

  const saveAttempts = (data) => {
    try { localStorage.setItem(getAttemptKey(), JSON.stringify(data)); } catch {}
  };

  const getAttemptCount = (certId, mode) => {
    const data = loadAttempts();
    return data[certId]?.[mode] || 0;
  };

  const recordAttempt = (certId, mode) => {
    const data = loadAttempts();
    if (!data[certId]) data[certId] = {};
    data[certId][mode] = (data[certId][mode] || 0) + 1;
    saveAttempts(data);
  };

  // Returns true when the user is allowed to start; false + shows CTA when blocked.
  const enforceAttemptLimit = (mode) => {
    const cId = selectedCertification?.id;
    if (!cId) return true;
    if (!FREE_TIER_CERTS.has(cId)) return true;
    if (userTier !== 'free') return true;
    // Free open beta — unlimited attempts, nothing to upsell to.
    if (userBetaMode) return true;
    const used = getAttemptCount(cId, mode);
    if (used < 1) return true;
    if (window.confirm(
      `You've used your 1 free ${mode} attempt for ${selectedCertification?.name || cId}.\n\nSubscribe for $14.99/month to unlock unlimited quiz and exam attempts on this cert. Open subscribe page?`
    )) {
      openExamSubscribe(selectedCertification?.code);
    }
    return false;
  };

  // Exam results
  const [examResults, setExamResults] = useState(null); // { passed, percentage, correct, total, timeTakenSeconds }
  const [showExamResults, setShowExamResults] = useState(false);
  const [reviewMode, setReviewMode] = useState(false); // reviewing answers after exam
  const [showQuitConfirm, setShowQuitConfirm] = useState(false);
  // True when a server save is rejected because the session expired and can't
  // refresh (notably SSO sessions with no refresh token). Progress is still
  // saved locally; the banner tells the user to sign in again to sync.
  const [sessionExpired, setSessionExpired] = useState(false);

  const isAdmin = ADMIN_EMAILS.includes(user?.username) || ADMIN_EMAILS.includes(user?.signInDetails?.loginId);

  // Check URL for direct cert link: ?exam=aif-c01 or ?quiz=aif-c01
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const examParam = params.get('exam');
    const quizParam = params.get('quiz');

    if (examParam) {
      const cert = AWS_CERTIFICATIONS.find(c => c.id === examParam);
      if (cert && cert.available) {
        setSelectedCertification(cert);
        setIsExamLink(true);
        fetchQuestionStats(cert.id);
        // Skip the landing page — go straight to "Before You Begin"
        setTimeout(() => {
          setShowExamDisclaimer(true);
        }, 300);
      }
    } else if (quizParam) {
      // Quiz link — show the "Before You Begin" screen directly, skip landing page
      const cert = AWS_CERTIFICATIONS.find(c => c.id === quizParam);
      if (cert && cert.available) {
        setSelectedCertification(cert);
        setIsExamLink(false);
        fetchQuestionStats(cert.id);
        setTimeout(() => {
          setShowQuizDisclaimer(true);
          window.history.replaceState({}, '', window.location.pathname);
        }, 300);
      }
    }
  }, []);

  // Fetch hidden certs from server (or localStorage for local dev)
  useEffect(() => {
    if (isAuthConfigured()) {
      fetch(`${API_URL}/api/settings/hidden-certs`)
        .then(r => r.json())
        .then(data => setHiddenCerts(data.hiddenCerts || []))
        .catch(() => {});
    } else {
      try {
        setHiddenCerts(JSON.parse(localStorage.getItem('hiddenCerts') || '[]'));
      } catch {}
    }
  }, []);

  useEffect(() => {
    if (isAuthConfigured()) {
      // If the other Dream and Build Cloud app navigated here with ?token=<idToken>,
      // bootstrap the Amplify session from that token before falling back to the
      // normal getCurrentUser check (which covers already-signed-in users).
      authBootstrapFromUrlToken()
        .then(ssoUser => ssoUser || authGetCurrentUser())
        .then(u => {
          // If neither path produced a signed-in user but the URL still has a
          // token (Learning Platform pool, our pool rejected the issuer), peek
          // the email out of that token so we can pre-fill the AuthScreen.
          // Best-effort and unverified — the email is only used as a form value.
          if (!u) {
            const ssoEmail = extractEmailFromUrlToken();
            if (ssoEmail) setPrefilledAuthEmail(ssoEmail);
          }
          setUser(u);
          setAuthChecked(true);
        });
    }
  }, []);

  // Load this user's per-user cert-access list any time the signed-in user changes.
  useEffect(() => {
    if (!isAuthConfigured() || !user) {
      setMyBlockedCerts([]);
      return;
    }
    (async () => {
      try {
        const token = await authGetToken();
        const res = await fetch(`${API_URL}/api/user/cert-access`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok) {
          const data = await res.json();
          setMyBlockedCerts(data.blockedCerts || []);
        }
      } catch (err) {
        console.error('Error loading user cert access:', err);
      }
    })();
  }, [user]);

  // Notify the parent frame (Dream and Build Cloud) to hide/show its chat
  // widget. On the cert selection screen (selectedCertification === null) the
  // chat icon should be invisible; inside a quiz it can reappear.
  useEffect(() => {
    if (window.parent && window.parent !== window) {
      window.parent.postMessage(
        { type: selectedCertification ? 'EXAM_APP_SHOW_CHAT' : 'EXAM_APP_HIDE_CHAT' },
        '*'
      );
    }
  }, [selectedCertification]);

  // Cert level filter for the selection screen (Foundational/Associate/Professional/Specialty)
  const [certLevelFilter, setCertLevelFilter] = useState('Foundational');

  useEffect(() => {
    fetchQuestionStats();
    loadQuizHistory();
  }, []);

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && showJumpDialog) {
        setShowJumpDialog(false);
        setJumpToNumber('');
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [showJumpDialog]);

  // Exam countdown timer
  useEffect(() => {
    if (examTimeLeft === null || examTimerPaused || examTimerExpired) return;
    if (examTimeLeft <= 0) {
      setExamTimerExpired(true);
      const examMins = selectedCertification?.examMinutes || 120;
      finishExam(score, answeredQuestions, examMins * 60);
      return;
    }
    const tick = setInterval(() => {
      setExamTimeLeft(prev => (prev !== null ? prev - 1 : null));
    }, 1000);
    return () => clearInterval(tick);
  }, [examTimeLeft, examTimerPaused, examTimerExpired]);

  const formatExamTime = (seconds) => {
    if (seconds === null) return '';
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = seconds % 60;
    if (h > 0) return `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
    return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  };

  const getTimerClass = (seconds) => {
    if (seconds === null) return '';
    if (seconds <= 300) return 'timer-critical';   // last 5 min — red
    if (seconds <= 900) return 'timer-warning';    // last 15 min — yellow
    return 'timer-ok';                             // green
  };

  // Screen lock: pause + warn when user leaves tab/minimizes during active exam
  useEffect(() => {
    if (!examActive || examTimerExpired) return;

    const handleVisibilityChange = () => {
      if (document.hidden) {
        setExamTimerPaused(true);
        setExamLeftWarning(true);
      }
    };

    const handleBeforeUnload = (e) => {
      e.preventDefault();
      e.returnValue = 'Your exam is in progress. Are you sure you want to leave?';
      return e.returnValue;
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [examActive, examTimerExpired]);

  // ── Copy protection ──────────────────────────────────────────────────────
  // Block right-click context menu and Ctrl/Cmd+C during active quiz/exam
  // sessions. Combined with user-select:none in CSS for layered protection.
  // Removed when the session ends so normal browsing is unaffected.
  useEffect(() => {
    if (!quizStarted) return;

    const blockContextMenu = (e) => {
      e.preventDefault();
      return false;
    };

    const blockCopy = (e) => {
      if ((e.ctrlKey || e.metaKey) && (e.key === 'c' || e.key === 'C' || e.key === 'a' || e.key === 'A')) {
        e.preventDefault();
        return false;
      }
    };

    const blockClipboard = (e) => {
      e.preventDefault();
      e.clipboardData?.setData('text/plain', '');
      return false;
    };

    document.addEventListener('contextmenu', blockContextMenu);
    document.addEventListener('keydown', blockCopy);
    document.addEventListener('copy', blockClipboard);

    return () => {
      document.removeEventListener('contextmenu', blockContextMenu);
      document.removeEventListener('keydown', blockCopy);
      document.removeEventListener('copy', blockClipboard);
    };
  }, [quizStarted]);

  // Overlay localStorage backups onto the server history so progress and the
  // "completed" flag survive a save that failed mid-quiz (e.g. an expired
  // session that can't refresh — notably SSO-bootstrapped sessions that have
  // no refresh token). For each quiz we keep whichever copy is more advanced
  // (completed, or more answered questions), and best-effort re-POST any local
  // copy the server is behind on so the backend catches up once the token is
  // valid again.
  const mergeAndSyncHistory = async (serverList, certId, token) => {
    const idOf = (q) => String(q.quizId ?? q.id);
    const answered = (q) => Object.keys(q.answeredQuestions || {}).length;
    let local = [];
    try { local = JSON.parse(localStorage.getItem('quizHistory') || '[]'); } catch {}
    if (certId) local = local.filter(q => (q.certId || 'ans-c01') === certId);

    const byId = new Map();
    for (const q of serverList) byId.set(idOf(q), q);

    const toResync = [];
    for (const lq of local) {
      const id = idOf(lq);
      const sq = byId.get(id);
      const localAhead = !sq || (lq.completed && !sq.completed) || answered(lq) > answered(sq);
      if (localAhead) {
        byId.set(id, sq ? { ...sq, ...lq, quizId: id } : { ...lq, quizId: id });
        toResync.push({ ...lq, quizId: id });
      }
    }

    if (token && toResync.length) {
      for (const lq of toResync) {
        fetch(`${API_URL}/api/history`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify(lq),
        }).catch(() => {});
      }
    }

    return Array.from(byId.values())
      .sort((a, b) => (b.lastUpdated || '').localeCompare(a.lastUpdated || ''));
  };

  const loadQuizHistory = async () => {
    if (isAuthConfigured()) {
      try {
        const token = await authGetToken();
        if (!token) return;
        const certId = selectedCertification?.id;
        const url = certId
          ? `${API_URL}/api/history?cert=${certId}`
          : `${API_URL}/api/history`;
        const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
        if (res.ok) {
          const data = await res.json();
          setQuizHistory(await mergeAndSyncHistory(data, certId, token));
        }
      } catch (error) {
        console.error('Error loading quiz history from API:', error);
      }
    } else {
      try {
        const history = localStorage.getItem('quizHistory');
        if (history) {
          const parsed = JSON.parse(history);
          // Purge entries older than 45 days
          const cutoff = Date.now() - 45 * 24 * 60 * 60 * 1000;
          const filtered = parsed.filter(q => {
            const ts = q.startedAt ? new Date(q.startedAt).getTime() : (q.id || 0);
            return ts > cutoff;
          });
          if (filtered.length !== parsed.length) {
            localStorage.setItem('quizHistory', JSON.stringify(filtered));
          }
          setQuizHistory(filtered);
        }
      } catch (error) {
        console.error('Error loading quiz history:', error);
      }
    }
  };

  const autoSaveQuiz = async (newIndex, newScore, newAnsweredQuestions, isCompleted = false, examResultData = null) => {
    if (!currentQuizId || questions.length === 0) return;

    // currentQuizId is the start-time epoch. It's a number for a fresh quiz
    // but a string when resumed from history (saved as String(...)). Guard the
    // Date conversion — new Date("<numeric string>") is an Invalid Date, and
    // calling .toISOString() on it throws, which previously aborted the whole
    // save so answers added to a resumed quiz never persisted.
    const startedAtMs = Number(currentQuizId);
    const startedAt = Number.isFinite(startedAtMs)
      ? new Date(startedAtMs).toISOString()
      : new Date().toISOString();

    const quizData = {
      quizId: String(currentQuizId),
      certId: selectedCertification?.id || 'ans-c01',
      mode: viewMode,
      questions,
      currentQuestionIndex: newIndex,
      score: newScore,
      answeredQuestions: newAnsweredQuestions,
      completed: isCompleted,
      startedAt,
      lastUpdated: new Date().toISOString(),
      isExamMode: examActive,
      examResult: examResultData,
    };

    // Always mirror progress to localStorage first, so answers survive even
    // when the server save fails — e.g. the auth session times out mid-quiz
    // and the token can't refresh, or the network drops. The resume flow
    // reconciles this local copy against the server copy (see
    // loadQuizFromHistory) and keeps whichever is more complete.
    try {
      const history = JSON.parse(localStorage.getItem('quizHistory') || '[]');
      const existingIndex = history.findIndex(q => String(q.id ?? q.quizId) === String(currentQuizId));
      const localData = { ...quizData, id: currentQuizId };
      if (existingIndex !== -1) history[existingIndex] = localData;
      else history.unshift(localData);
      const trimmed = history.slice(0, 50);
      localStorage.setItem('quizHistory', JSON.stringify(trimmed));
      // For anonymous users the local store *is* the visible history list.
      if (!isAuthConfigured()) setQuizHistory(trimmed);
    } catch (error) {
      console.error('Error writing local quiz backup:', error);
    }

    // Then persist to the server for signed-in users. If this fails (expired
    // session, offline), the localStorage mirror above already has the answers.
    if (isAuthConfigured()) {
      try {
        const token = await authGetToken();
        if (!token) {
          // Session expired and can't refresh — surface it (progress is saved
          // locally above and will re-sync once the user signs in again).
          setSessionExpired(true);
          return;
        }
        const res = await fetch(`${API_URL}/api/history`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify(quizData),
        });
        if (res.status === 401 || res.status === 403) {
          setSessionExpired(true);
        } else if (res.ok) {
          setSessionExpired(false);
          // Refresh history list
          loadQuizHistory();
        }
      } catch (error) {
        console.error('Error saving quiz to API:', error);
      }
    }
  };

  const fetchQuestionStats = async (certId) => {
    try {
      const cert = certId || selectedCertification?.id || 'ans-c01';
      const tok = await authGetToken();
      const response = await fetch(`${API_URL}/api/questions/stats?cert=${cert}`, {
        headers: tok ? { Authorization: `Bearer ${tok}` } : {},
      });
      if (response.status === 402) {
        const data = await response.json().catch(() => ({}));
        setQuestionStats({ total: 0, locked: true, requiredTier: data.requiredTier || 'pro' });
        return;
      }
      const data = await response.json();
      setQuestionStats(data);
    } catch (error) {
      console.error('Error fetching question stats:', error);
    }
  };

  const fetchQuestions = async (mode = 'random', start = null, end = null, skipHistorySave = false, count = 20, examMode = false) => {
    try {
      const cert = selectedCertification?.id || 'ans-c01';
      let url = `${API_URL}/api/questions?cert=${cert}`;
      if (mode === 'all') {
        url += '&mode=all';
      } else if (mode === 'range' && start && end) {
        url += `&mode=range&start=${start}&end=${end}`;
      } else {
        url += `&count=${count}`;
        // Tell the API this is a full timed-exam pull so it can enforce the
        // quiz-score unlock server-side (the client gate alone is bypassable).
        if (examMode) url += '&mode=exam';
      }
      
      const tok = await authGetToken();
      const response = await fetch(url, {
        headers: tok ? { Authorization: `Bearer ${tok}` } : {},
      });
      if (response.status === 403) {
        const lockData = await response.json().catch(() => ({}));
        if (lockData.code === 'EXAM_LOCKED') {
          window.alert(lockData.error || 'Score 80% or higher on the practice quiz to unlock this exam.');
          setQuizStarted(false);
          return;
        }
      }
      if (response.status === 402) {
        const data = await response.json().catch(() => ({}));
        if (window.confirm(`This cert requires a $14.99/month exam subscription. Open subscribe page?`)) {
          openExamSubscribe(selectedCertification?.code);
        }
        setQuizStarted(false);
        return;
      }
      const data = await response.json();
      setQuestions(data);
      setCurrentQuestionIndex(0);
      setSelectedAnswers([]);
      setShowResult(false);
      setResult(null);
      setScore({ correct: 0, total: 0 });
      setAnsweredQuestions({});
      setCurrentQuizId(Date.now());
    } catch (error) {
      console.error('Error fetching questions:', error);
    }
  };

  const handleAnswerSelect = (letter) => {
    if (showResult) return;
    
    const currentQuestion = questions[currentQuestionIndex];
    const requiredCount = getRequiredAnswerCount(currentQuestion);
    
    if (requiredCount === 1) {
      // Single selection mode
      setSelectedAnswers([letter]);
    } else {
      // Multiple selection mode
      setSelectedAnswers(prev => {
        if (prev.includes(letter)) {
          // Deselect if already selected
          return prev.filter(l => l !== letter);
        } else if (prev.length < requiredCount) {
          // Add if under the limit
          return [...prev, letter];
        } else {
          // Replace oldest selection if at limit
          return [...prev.slice(1), letter];
        }
      });
    }
  };

  const submitAnswer = async () => {
    const currentQuestion = questions[currentQuestionIndex];
    const requiredCount = getRequiredAnswerCount(currentQuestion);
    
    if (selectedAnswers.length !== requiredCount) return;

    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/check-answer`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          questionId: currentQuestion.id,
          question: currentQuestion.question,
          options: currentQuestion.options,
          selectedAnswers: selectedAnswers,
          requiredCount: requiredCount,
          cert: selectedCertification?.id || 'ans-c01'
        })
      });

      const data = await response.json();
      setResult(data);
      // Only reveal correct/incorrect in quiz mode.
      // In exam mode, feedback is shown on the results screen after submission.
      if (!examActive) setShowResult(true);
      
      // Update score
      const newScore = {
        correct: score.correct + (data.isCorrect ? 1 : 0),
        total: score.total + 1
      };
      setScore(newScore);
      
      // Save answered question
      const newAnsweredQuestions = {
        ...answeredQuestions,
        [currentQuestionIndex]: {
          selectedAnswers,
          result: data,
          timestamp: new Date().toISOString()
        }
      };
      setAnsweredQuestions(newAnsweredQuestions);
      
      // Auto-save quiz progress after answering
      autoSaveQuiz(currentQuestionIndex, newScore, newAnsweredQuestions, false);

      // Exam mode shows no per-question feedback, so submitting must do
      // something visible: record the answer and advance to the next
      // question (or finish on the last one). Without this the "Submit
      // Answer" button appears to do nothing.
      if (examActive) {
        if (currentQuestionIndex < questions.length - 1) {
          const nextIndex = currentQuestionIndex + 1;
          setCurrentQuestionIndex(nextIndex);
          setSelectedAnswers(newAnsweredQuestions[nextIndex]?.selectedAnswers || []);
          setShowResult(false);
          setResult(null);
          autoSaveQuiz(nextIndex, newScore, newAnsweredQuestions, false);
        } else {
          const timeTaken = examTimeLeft !== null
            ? (selectedCertification?.examMinutes || 120) * 60 - examTimeLeft
            : null;
          finishExam(newScore, newAnsweredQuestions, timeTaken);
        }
      }
    } catch (error) {
      console.error('Error checking answer:', error);
      alert('Error checking answer. Make sure the server is running and AWS credentials are configured.');
    }
    setLoading(false);
  };

  const nextQuestion = () => {
    if (currentQuestionIndex < questions.length - 1) {
      const newIndex = currentQuestionIndex + 1;
      setCurrentQuestionIndex(newIndex);
      
      // Auto-save progress when moving to next question
      autoSaveQuiz(newIndex, score, answeredQuestions, false);
      
      // Check if this question was already answered
      if (answeredQuestions[newIndex]) {
        setSelectedAnswers(answeredQuestions[newIndex].selectedAnswers);
        setResult(answeredQuestions[newIndex].result);
        setShowResult(true);
      } else {
        setSelectedAnswers([]);
        setShowResult(false);
        setResult(null);
      }
    } else {
      // Quiz/exam completed
      if (examActive) {
        const timeTaken = examTimeLeft !== null
          ? (selectedCertification?.examMinutes || 120) * 60 - examTimeLeft
          : null;
        finishExam(score, answeredQuestions, timeTaken);
      } else {
        finishQuiz(score, answeredQuestions);
      }
    }
  };

  const resetQuiz = () => {
    // Current quiz is already auto-saved, just start fresh
    fetchQuestions('random', null, null, true);
    setCurrentQuestionIndex(0);
    setSelectedAnswers([]);
    setShowResult(false);
    setResult(null);
    setScore({ correct: 0, total: 0 });
    setAnsweredQuestions({});
    setViewMode('quiz');
    setSidePanelOpen(false);
    setExamTimeLeft(null);
    setExamTimerPaused(false);
    setExamTimerExpired(false);
    setExamActive(false);
    setExamLeftWarning(false);
    setExamResults(null);
    setShowExamResults(false);
    setReviewMode(false);
    setShowQuizDisclaimer(false);
  };

  const handleModeChange = (mode) => {
    setViewMode(mode);
    setSidePanelOpen(false);
    if (mode === 'quiz') {
      fetchQuestions('random');
      setQuizStarted(true);
    } else if (mode === 'browse') {
      fetchQuestions('all');
      setQuizStarted(true);
    } else if (mode === 'history') {
      loadQuizHistory();
      setQuizStarted(true);
    }
  };

  // Tier-gate guard — owner bypass lives in hasAccessToCert.
  const enforceCertTier = () => {
    const certCode = selectedCertification?.code;
    if (!certCode) return true;
    if (hasAccessToCert(userTier, certCode, userTierEmail, accessOpts)) return true;
    if (window.confirm(`Unlock ${certCode} for $14.99/month. Open subscribe page?`)) {
      openExamSubscribe(certCode);
    }
    return false;
  };

  // ── Exam unlock gate ──────────────────────────────────────────────────
  // The full timed exam stays locked for a cert until the learner scores
  // QUIZ_UNLOCK_PERCENT or better on that cert's 20-question quiz. After that
  // it's unlocked permanently for that cert and can be retaken without limit.
  //
  // Unlock is DERIVED from saved attempt history rather than stored as its own
  // flag: no migration, it works across devices (authed history comes from the
  // API), and learners who already passed a quiz get retroactive credit.
  // This gate is additive — tier access (enforceCertTier) and the free-tier
  // attempt limit still apply on top of it.
  const QUIZ_UNLOCK_PERCENT = 80;

  const bestQuizPercentForCert = (certId) => {
    const target = certId || 'ans-c01';
    return (quizHistory || []).reduce((best, q) => {
      if ((q.certId || 'ans-c01') !== target) return best;
      if (q.isExamMode) return best;      // quizzes only, not exam attempts
      if (!q.completed) return best;      // finished attempts only
      const total = Array.isArray(q.questions) ? q.questions.length : 0;
      if (!total) return best;
      const pct = ((Number(q.score) || 0) / total) * 100;
      return Math.max(best, pct);
    }, 0);
  };

  const bestQuizPercent = bestQuizPercentForCert(selectedCertification?.id);
  // Admins/owners bypass the gate, same as every other gate in this app.
  const examUnlocked = isAdmin || bestQuizPercent >= QUIZ_UNLOCK_PERCENT;

  // Always runs the Quiz path (untimed, 20 questions, immediate feedback).
  // Used by the new "Start Quiz" button that shows alongside Start Exam.
  const startQuizMode = () => {
    if (!enforceCertTier()) return;
    setShowQuizDisclaimer(true);
  };

  const confirmStartExam = () => {
    // Belt-and-braces: never start an exam that hasn't been unlocked.
    if (!examUnlocked) { setShowExamDisclaimer(false); return; }
    if (!enforceAttemptLimit('exam')) return;
    recordAttempt(selectedCertification?.id, 'exam');
    const examQ = selectedCertification?.examQuestions || 65;
    const examMins = selectedCertification?.examMinutes || 120;
    setShowExamDisclaimer(false);
    fetchQuestions('random', null, null, false, examQ, true);
    setExamTimeLeft(examMins * 60);
    setExamTimerPaused(false);
    setExamTimerExpired(false);
    setExamActive(true);
    setExamLeftWarning(false);
    setQuizStarted(true);
    setViewMode('quiz');
  };

  const confirmStartQuiz = () => {
    if (!enforceAttemptLimit('quiz')) return;
    recordAttempt(selectedCertification?.id, 'quiz');
    setShowQuizDisclaimer(false);
    fetchQuestions('random', null, null, false, 20);
    setExamTimeLeft(null);
    setExamTimerExpired(false);
    setExamActive(false);
    setQuizStarted(true);
    setViewMode('quiz');
  };

  // Called when exam ends (all questions answered OR timer expires)
  const finishExam = (finalScore, finalAnsweredQuestions, timeTakenSeconds) => {
    // Cert-specific passing thresholds (AWS scaled score -> raw % equivalent).
    // Foundational: 700/1000 = 70%, Associate: 720/1000 = 72%,
    // Professional + Specialty: 750/1000 = 75%.
    const CERT_PASS_THRESHOLDS = {
      'clf-c02': 73, 'aif-c01': 73,
      'saa-c03': 75, 'dva-c02': 75, 'soa-c02': 75, 'soa-c03': 75,
      'dea-c01': 75, 'mla-c01': 75,
      'sap-c02': 78, 'dop-c02': 78, 'aip-c01': 78,
      'ans-c01': 78, 'scs-c02': 78,
    };
    const certId = selectedCertification?.id || 'saa-c03';
    const PASS_THRESHOLD = CERT_PASS_THRESHOLDS[certId] ?? 75;
    const percentage = finalScore.total > 0
      ? Math.round((finalScore.correct / finalScore.total) * 100)
      : 0;
    const passed = percentage >= PASS_THRESHOLD;
    const resultData = { passed, percentage, correct: finalScore.correct, total: finalScore.total, timeTakenSeconds };
    setExamResults(resultData);
    setShowExamResults(true);
    setExamActive(false);
    setExamTimerExpired(false); // dismiss the timer-expired overlay, results modal takes over
    autoSaveQuiz(questions.length - 1, finalScore, finalAnsweredQuestions, true, resultData);
  };

  // Enter post-exam review: page through every question from the start with
  // per-question feedback. Exams keep viewMode === 'quiz', so we reset the
  // index to the first question and load its recorded answer/result.
  const enterReviewMode = () => {
    setShowExamResults(false);
    setReviewMode(true);
    setExamActive(false);
    setExamTimeLeft(null);
    setExamTimerPaused(false);
    setCurrentQuestionIndex(0);

    const first = answeredQuestions[0];
    if (first) {
      setSelectedAnswers(first.selectedAnswers || []);
      setResult(first.result || null);
      setShowResult(true);
    } else {
      setSelectedAnswers([]);
      setResult(null);
      setShowResult(false);
    }
  };

  // Called when a (non-exam) quiz reaches the last question and the user
  // clicks "Finish Quiz". Reuses the exam results + review experience, but
  // without pass/fail framing or a timer.
  const finishQuiz = (finalScore, finalAnsweredQuestions) => {
    const percentage = finalScore.total > 0
      ? Math.round((finalScore.correct / finalScore.total) * 100)
      : 0;
    const resultData = {
      isQuiz: true,
      passed: null,
      percentage,
      correct: finalScore.correct,
      total: finalScore.total,
      timeTakenSeconds: null,
    };
    setExamResults(resultData);
    setShowExamResults(true);
    autoSaveQuiz(questions.length - 1, finalScore, finalAnsweredQuestions, true, resultData);
  };

  // Quit exam early — unanswered questions count as incorrect
  const quitExam = () => {
    setShowQuitConfirm(false);
    setExamTimerPaused(false);

    // Build a complete answeredQuestions map: unanswered = marked incorrect
    const fullAnswered = { ...answeredQuestions };
    questions.forEach((_, idx) => {
      if (!fullAnswered[idx]) {
        fullAnswered[idx] = {
          selectedAnswers: [],
          result: { isCorrect: false, correctAnswers: [], explanation: 'Not answered — marked incorrect.' },
          timestamp: new Date().toISOString(),
          skipped: true,
        };
      }
    });

    const correct = Object.values(fullAnswered).filter(a => a.result?.isCorrect).length;
    const finalScore = { correct, total: questions.length };
    const timeTaken = examTimeLeft !== null
      ? (selectedCertification?.examMinutes || 120) * 60 - examTimeLeft
      : null;

    finishExam(finalScore, fullAnswered, timeTaken);
  };

  const handleJumpToQuestion = (e) => {
    e.preventDefault();
    const questionNum = parseInt(jumpToNumber);
    
    if (!questionNum || questionNum < 1) {
      alert('Please enter a valid question number');
      return;
    }

    // Find the index of the question with this ID
    const questionIndex = questions.findIndex(q => q.id === questionNum);
    
    if (questionIndex === -1) {
      alert(`Question #${questionNum} not found in current set`);
      return;
    }

    setCurrentQuestionIndex(questionIndex);
    
    // Check if this question was already answered
    if (answeredQuestions[questionIndex]) {
      setSelectedAnswers(answeredQuestions[questionIndex].selectedAnswers);
      setResult(answeredQuestions[questionIndex].result);
      setShowResult(true);
    } else {
      setSelectedAnswers([]);
      setShowResult(false);
      setResult(null);
    }
    
    setShowJumpDialog(false);
    setJumpToNumber('');
  };

  const loadQuizFromHistory = (quizArg) => {
    // Reconcile with the local backup: a mid-quiz timeout can prevent the last
    // answers from reaching the server, so the localStorage copy may be more
    // complete. Prefer whichever has more answered questions (tie-break on the
    // most recent update), then continue with that copy.
    let quiz = quizArg;
    try {
      const id = String(quizArg.quizId ?? quizArg.id);
      const local = JSON.parse(localStorage.getItem('quizHistory') || '[]')
        .find(q => String(q.id ?? q.quizId) === id);
      if (local) {
        const localCount = Object.keys(local.answeredQuestions || {}).length;
        const serverCount = Object.keys(quizArg.answeredQuestions || {}).length;
        const localNewer = (local.lastUpdated || '') >= (quizArg.lastUpdated || '');
        if (localCount > serverCount || (localCount === serverCount && localCount > 0 && localNewer)) {
          quiz = { ...quizArg, ...local, quizId: quizArg.quizId ?? quizArg.id ?? local.id };
        }
      }
    } catch (e) {
      // Fall back to the passed-in (server) copy on any parse error.
    }

    setQuestions(quiz.questions);
    setScore(quiz.score);
    setAnsweredQuestions(quiz.answeredQuestions || {});
    setCurrentQuizId(quiz.quizId || quiz.id);
    setViewMode(quiz.mode || 'quiz');
    setQuizStarted(true);
    setSidePanelOpen(false);
    
    // Find the first unanswered question to resume from
    const answeredKeys = Object.keys(quiz.answeredQuestions || {}).map(Number);
    let resumeIndex = quiz.currentQuestionIndex;
    
    if (!quiz.completed && answeredKeys.length > 0) {
      // Find first unanswered question
      for (let i = 0; i < quiz.questions.length; i++) {
        if (!quiz.answeredQuestions[i]) {
          resumeIndex = i;
          break;
        }
      }
    }
    
    setCurrentQuestionIndex(resumeIndex);
    
    // If this question was already answered, show the result
    if (quiz.answeredQuestions && quiz.answeredQuestions[resumeIndex]) {
      setSelectedAnswers(quiz.answeredQuestions[resumeIndex].selectedAnswers);
      setResult(quiz.answeredQuestions[resumeIndex].result);
      setShowResult(true);
    } else {
      setSelectedAnswers([]);
      setShowResult(false);
      setResult(null);
    }
  };

  const deleteQuizFromHistory = async (quizId) => {
    if (isAuthConfigured()) {
      try {
        const token = await authGetToken();
        if (!token) return;
        await fetch(`${API_URL}/api/history/${quizId}`, {
          method: 'DELETE',
          headers: { Authorization: `Bearer ${token}` },
        });
        loadQuizHistory();
      } catch (error) {
        console.error('Error deleting quiz:', error);
      }
    } else {
      try {
        const history = JSON.parse(localStorage.getItem('quizHistory') || '[]');
        const filtered = history.filter(q => q.quizId !== quizId);
        localStorage.setItem('quizHistory', JSON.stringify(filtered));
        setQuizHistory(filtered);
      } catch (error) {
        console.error('Error deleting quiz:', error);
      }
    }
  };

  // Fetch domain breakdown for a quiz's questions
  const fetchDomainStatsForQuiz = async (quiz) => {
    try {
      const cert = selectedCertification?.id || 'ans-c01';
      const questionIds = quiz.questions.map(q => q.id);
      const response = await fetch(`${API_URL}/api/questions/domains-for-ids`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ questionIds, cert })
      });
      const questionDomains = await response.json();
      
      // Build domain performance dynamically from the response
      const domainPerformance = {};
      
      quiz.questions.forEach((question, index) => {
        const domain = questionDomains[question.id];
        if (domain) {
          if (!domainPerformance[domain]) {
            domainPerformance[domain] = { correct: 0, total: 0 };
          }
          const answered = quiz.answeredQuestions[index];
          if (answered) {
            domainPerformance[domain].total++;
            if (answered.result?.isCorrect) {
              domainPerformance[domain].correct++;
            }
          }
        }
      });
      
      return domainPerformance;
    } catch (error) {
      console.error('Error fetching domain stats:', error);
      return null;
    }
  };

  // Start a sub-quiz with only wrong answers from a completed quiz
  const startWrongAnswersQuiz = (quiz) => {
    // Find all wrong answers
    const wrongQuestions = [];
    quiz.questions.forEach((question, index) => {
      const answered = quiz.answeredQuestions[index];
      if (answered && !answered.result?.isCorrect) {
        wrongQuestions.push(question);
      }
    });
    
    if (wrongQuestions.length === 0) {
      alert('No wrong answers to retry! You got them all correct.');
      return;
    }
    
    // Start new quiz with wrong questions
    setQuestions(wrongQuestions);
    setCurrentQuestionIndex(0);
    setSelectedAnswers([]);
    setShowResult(false);
    setResult(null);
    setScore({ correct: 0, total: 0 });
    setAnsweredQuestions({});
    setCurrentQuizId(Date.now());
    setViewMode('quiz');
    setQuizStarted(true);
    setSidePanelOpen(false);
  };

  const handleRangeSubmit = (e) => {
    e.preventDefault();
    if (rangeStart && rangeEnd) {
      fetchQuestions('range', rangeStart, rangeEnd);
      setViewMode('range');
      setQuizStarted(true);
      setSidePanelOpen(false);
    }
  };

  const handleCertificationSelect = (cert) => {
    if (!hasAccessToCert(userTier, cert.code, userTierEmail, accessOpts)) {
      // Locked cert: the card still renders cursor:pointer, so a silent
      // no-op here made the whole card feel broken. Clicking a locked card
      // now opens the free 10-question preview — the useful next step, and
      // it surfaces the inline UpgradeBlock alongside it.
      openPreviewModal(cert);
      return;
    }
    if (cert.available) {
      setSelectedCertification(cert);
      setQuizStarted(false);
      setQuestions([]);
      setViewMode('quiz');
      setIsExamLink(true);
      fetchQuestionStats(cert.id);
    }
  };

  const goBackToCertSelection = () => {
    setSelectedCertification(null);
    setQuizStarted(false);
    setExamTimeLeft(null);
    setExamTimerPaused(false);
    setExamTimerExpired(false);
    setExamActive(false);
    setExamLeftWarning(false);
    setShowExamDisclaimer(false);
    setExamResults(null);
    setShowExamResults(false);
    setReviewMode(false);
    setIsExamLink(false);
    // Clear exam param from URL if present
    if (window.location.search.includes('exam=')) {
      window.history.replaceState({}, '', window.location.pathname);
    }
  };

  const navigateQuestion = (direction) => {
    const newIndex = direction === 'next' 
      ? Math.min(currentQuestionIndex + 1, questions.length - 1)
      : Math.max(currentQuestionIndex - 1, 0);
    
    setCurrentQuestionIndex(newIndex);
    
    // Check if this question was already answered
    if (answeredQuestions[newIndex]) {
      setSelectedAnswers(answeredQuestions[newIndex].selectedAnswers);
      setResult(answeredQuestions[newIndex].result);
      setShowResult(true);
    } else {
      setSelectedAnswers([]);
      setShowResult(false);
      setResult(null);
    }
  };

  const toggleCertVisibility = async (certId) => {
    const next = hiddenCerts.includes(certId)
      ? hiddenCerts.filter(id => id !== certId)
      : [...hiddenCerts, certId];
    setHiddenCerts(next);

    if (isAuthConfigured()) {
      try {
        const token = await authGetToken();
        await fetch(`${API_URL}/api/admin/settings`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({ hiddenCerts: next }),
        });
      } catch (err) {
        console.error('Error saving hidden certs:', err);
      }
    } else {
      localStorage.setItem('hiddenCerts', JSON.stringify(next));
    }
  };

  const copyExamLink = (certId) => {
    const url = `${window.location.origin}${window.location.pathname}?exam=${certId}`;
    navigator.clipboard.writeText(url).then(() => {
      setCopiedCertId(certId);
      setTimeout(() => setCopiedCertId(null), 2000);
    });
  };

  const downloadQuizHtml = async (cert) => {
    try {
      // Fetch random questions for this cert
      const tok = await authGetToken();
      const response = await fetch(`${API_URL}/api/questions?cert=${cert.id}&mode=random&count=${quizDownloadCount}`, {
        headers: tok ? { Authorization: `Bearer ${tok}` } : {},
      });
      const questions = await response.json();

      if (!Array.isArray(questions) || questions.length === 0) {
        alert('No questions available for this certification yet. Please try again later.');
        return;
      }

      // Fetch answers for these questions
      const answersResponse = await fetch(`${API_URL}/api/answers-batch`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          questionIds: questions.map(q => q.id),
          cert: cert.id
        })
      });
      const answers = await answersResponse.json();
      
      // Generate the HTML
      const html = generateQuizHtml(cert, questions, answers);
      
      // Download the file
      const blob = new Blob([html], { type: 'text/html' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${cert.code}-practice-quiz-${new Date().toISOString().slice(0, 10)}.html`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error generating quiz:', err);
      alert('Failed to generate quiz. Please try again.');
    }
  };

  const generateQuizHtml = (cert, questions, answers) => {
    // Prepare questions data with proper escaping for JSON
    const questionsData = questions.map((q) => ({
      id: q.id,
      question: q.question,
      options: q.options,
      isHotspot: q.isHotspot || false,
      image: q.image || null,
      answerImage: q.answerImage || null,
      correctAnswers: answers[q.id]?.correctAnswers || [],
      explanation: answers[q.id]?.explanation || 'No explanation available.',
      perOptionRationales: answers[q.id]?.perOptionRationales || null,
      concepts: answers[q.id]?.concepts || [],
      documentationUrl: answers[q.id]?.documentationUrl || '',
      documentationLabel: answers[q.id]?.documentationLabel || '',
    }));
    const questionsJson = JSON.stringify(questionsData);
    const totalQ = questionsData.length;

    return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>${cert.name} - Practice Quiz</title>
  <style>
    * { box-sizing: border-box; margin: 0; padding: 0; }
    body {
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
      background: #f5f5f5;
      color: #333;
      line-height: 1.6;
    }
    .container {
      max-width: 800px;
      margin: 0 auto;
      padding: 20px;
    }
    header {
      background: #232f3e;
      color: white;
      padding: 30px 20px;
      text-align: center;
      margin-bottom: 30px;
      border-radius: 8px;
    }
    header h1 { font-size: 1.5rem; margin-bottom: 8px; }
    header p { opacity: 0.9; font-size: 0.95rem; }
    .score-bar {
      background: white;
      padding: 15px 20px;
      border-radius: 8px;
      margin-bottom: 20px;
      display: flex;
      justify-content: space-between;
      align-items: center;
      box-shadow: 0 2px 4px rgba(0,0,0,0.1);
    }
    .score { font-weight: bold; font-size: 1.1rem; }
    .progress { color: #666; }
    .question-card {
      background: white;
      border-radius: 8px;
      padding: 25px;
      margin-bottom: 20px;
      box-shadow: 0 2px 4px rgba(0,0,0,0.1);
    }
    .question-number {
      color: #ff9900;
      font-weight: bold;
      font-size: 0.9rem;
      margin-bottom: 10px;
    }
    .question-text {
      font-size: 1.1rem;
      margin-bottom: 20px;
      color: #232f3e;
      white-space: pre-wrap;
    }
    .multi-hint {
      background: #fff3cd;
      color: #856404;
      padding: 10px 15px;
      border-radius: 5px;
      margin-bottom: 15px;
      font-weight: 500;
      font-size: 0.9rem;
    }
    .options { display: flex; flex-direction: column; gap: 10px; }
    .option {
      display: flex;
      align-items: flex-start;
      padding: 15px;
      border: 2px solid #e0e0e0;
      border-radius: 8px;
      cursor: pointer;
      transition: all 0.2s;
    }
    .option:hover:not(.disabled) { border-color: #007bff; background: #f8f9ff; }
    .option.selected { border-color: #007bff; background: #e3f2fd; }
    .option.correct { border-color: #28a745 !important; background: #d4edda !important; cursor: default; }
    .option.incorrect { border-color: #dc3545 !important; background: #f8d7da !important; cursor: default; }
    .option.disabled { cursor: default; pointer-events: none; }
    .option-letter {
      font-weight: bold;
      margin-right: 12px;
      min-width: 24px;
      color: #232f3e;
    }
    .option-text { flex: 1; }
    .submit-btn, .next-btn {
      background: #ff9900;
      color: white;
      border: none;
      padding: 12px 30px;
      font-size: 1rem;
      border-radius: 5px;
      cursor: pointer;
      margin-top: 15px;
      transition: background 0.2s;
    }
    .submit-btn:hover, .next-btn:hover { background: #e68900; }
    .submit-btn:disabled {
      background: #ccc;
      cursor: not-allowed;
    }
    .result {
      margin-top: 20px;
      padding: 20px;
      border-radius: 8px;
    }
    .result.correct-result { background: #d4edda; border: 1px solid #c3e6cb; }
    .result.incorrect-result { background: #f8d7da; border: 1px solid #f5c6cb; }
    .result-status {
      font-weight: bold;
      font-size: 1.2rem;
      margin-bottom: 15px;
    }
    .result-status.correct { color: #155724; }
    .result-status.incorrect { color: #721c24; }
    .correct-answer {
      background: #e3f2fd;
      padding: 12px 15px;
      border-radius: 5px;
      margin-bottom: 15px;
      font-weight: 500;
    }
    .explanation { color: #333; margin-bottom: 15px; }
    .explanation h4 { margin-bottom: 8px; color: #232f3e; }
    .explanation p { white-space: pre-wrap; line-height: 1.7; }
    .concepts {
      background: #f8f9fa;
      padding: 15px;
      border-radius: 5px;
      margin-bottom: 15px;
    }
    .concepts h4 { margin-bottom: 10px; color: #232f3e; font-size: 0.95rem; }
    .per-option-rationales { margin-bottom: 15px; }
    .per-option-rationales h4 { margin-bottom: 10px; color: #232f3e; font-size: 0.95rem; }
    .per-option-rationales ul { list-style: none; margin: 0; padding: 0; }
    .per-option-rationales li {
      padding: 10px 12px;
      margin-bottom: 8px;
      border-left: 4px solid #ccc;
      border-radius: 4px;
      background: #fafafa;
      font-size: 0.9rem;
      line-height: 1.6;
      color: #333;
    }
    .per-option-rationales li.rationale-correct { border-left-color: #1d8102; background: #f0f9ee; }
    .per-option-rationales li.rationale-incorrect { border-left-color: #d13212; background: #fdf3f1; }
    .concepts ul { margin: 0; padding-left: 20px; }
    .concepts li { margin-bottom: 4px; font-size: 0.9rem; color: #555; }
    .aws-link {
      display: inline-block;
      margin-top: 10px;
      padding: 8px 16px;
      background: #232f3e;
      color: white;
      text-decoration: none;
      border-radius: 5px;
      font-size: 0.9rem;
    }
    .aws-link:hover { background: #1a2332; }
    .final-score {
      background: white;
      padding: 40px;
      border-radius: 8px;
      text-align: center;
      box-shadow: 0 2px 4px rgba(0,0,0,0.1);
    }
    .final-score h2 { color: #232f3e; margin-bottom: 20px; }
    .final-score .big-score {
      font-size: 3rem;
      font-weight: bold;
      color: #ff9900;
      margin-bottom: 10px;
    }
    .final-score .percentage {
      font-size: 1.5rem;
      color: #666;
      margin-bottom: 30px;
    }
    .restart-btn {
      background: #232f3e;
      color: white;
      border: none;
      padding: 15px 40px;
      font-size: 1.1rem;
      border-radius: 5px;
      cursor: pointer;
    }
    .restart-btn:hover { background: #1a2332; }
    .hotspot-image {
      max-width: 100%;
      border-radius: 8px;
      margin: 15px 0;
      border: 1px solid #ddd;
    }
    .hidden { display: none !important; }
  </style>
</head>
<body>
  <div class="container">
    <header>
      <h1>${cert.name}</h1>
      <p>Practice Quiz - ${totalQ} Questions</p>
    </header>
    
    <div class="score-bar" id="scoreBar">
      <span class="score">Score: <span id="correctCount">0</span>/${totalQ}</span>
      <span class="progress">Question <span id="currentQ">1</span> of ${totalQ}</span>
    </div>
    
    <div id="questionsContainer"></div>
    
    <div class="final-score hidden" id="finalScore">
      <h2>Quiz Complete!</h2>
      <div class="big-score" id="finalScoreNum">0/${totalQ}</div>
      <div class="percentage" id="finalPercentage">0%</div>
      <button class="restart-btn" onclick="restartQuiz()">Try Again</button>
    </div>
  </div>

  <script>
    const questions = ${questionsJson};
    const totalQuestions = questions.length;
    let currentQuestion = 0;
    let score = 0;
    let selectedAnswers = [];
    let answered = [];

    function init() {
      const container = document.getElementById('questionsContainer');
      container.innerHTML = '';
      
      questions.forEach((q, idx) => {
        const requiredCount = getRequiredCount(q.question);
        const isMulti = requiredCount > 1;
        
        const card = document.createElement('div');
        card.className = 'question-card' + (idx === 0 ? '' : ' hidden');
        card.id = 'q' + idx;
        
        let optionsHtml = '';
        if (!q.isHotspot) {
          optionsHtml = '<div class="options" id="options' + idx + '">';
          q.options.forEach(function(opt) {
            optionsHtml += '<div class="option" data-letter="' + opt.letter + '" data-qidx="' + idx + '">' +
              '<span class="option-letter">' + opt.letter + '.</span>' +
              '<span class="option-text">' + escapeHtml(opt.text) + '</span></div>';
          });
          optionsHtml += '</div>';
        }
        
        let imageHtml = q.isHotspot && q.image ? '<img src="' + q.image + '" class="hotspot-image" alt="Question image">' : '';
        
        card.innerHTML = 
          '<div class="question-number">Question ' + (idx + 1) + ' of ' + totalQuestions + '</div>' +
          (isMulti ? '<div class="multi-hint">Select ' + requiredCount + ' answers</div>' : '') +
          '<div class="question-text">' + escapeHtml(q.question) + '</div>' +
          imageHtml +
          optionsHtml +
          '<div class="btn-container">' +
          '<button class="submit-btn" id="submit' + idx + '" ' + (q.isHotspot ? '' : 'disabled') + '>' + 
          (q.isHotspot ? 'Reveal Answer' : 'Submit Answer') + '</button>' +
          '<button class="next-btn hidden" id="next' + idx + '">' +
          (idx < totalQuestions - 1 ? 'Next Question' : 'See Results') + '</button>' +
          '</div>' +
          '<div class="result hidden" id="result' + idx + '"></div>';
        
        container.appendChild(card);
      });
      
      // Add click listeners for options
      document.querySelectorAll('.option').forEach(function(opt) {
        opt.addEventListener('click', function() {
          var qIdx = parseInt(this.dataset.qidx);
          var letter = this.dataset.letter;
          selectOption(qIdx, letter);
        });
      });
      
      // Add click listeners for submit buttons
      questions.forEach(function(q, idx) {
        document.getElementById('submit' + idx).addEventListener('click', function() {
          submitAnswer(idx);
        });
        document.getElementById('next' + idx).addEventListener('click', function() {
          nextQuestion(idx);
        });
      });
      
      selectedAnswers = questions.map(function() { return []; });
      answered = questions.map(function() { return false; });
    }

    function getRequiredCount(text) {
      if (!text) return 1;
      var lower = text.toLowerCase();
      var match = lower.match(/\b(?:choose|chose|select|pick)\s+(two|three|four|five|2|3|4|5)\b/);
      if (!match) return 1;
      var map = { two: 2, three: 3, four: 4, five: 5, '2': 2, '3': 3, '4': 4, '5': 5 };
      return map[match[1]] || 1;
    }

    function escapeHtml(text) {
      if (!text) return '';
      var div = document.createElement('div');
      div.textContent = text;
      return div.innerHTML;
    }

    function selectOption(qIdx, letter) {
      if (answered[qIdx]) return;
      
      var q = questions[qIdx];
      var required = getRequiredCount(q.question);
      
      if (required === 1) {
        selectedAnswers[qIdx] = [letter];
      } else {
        var idx = selectedAnswers[qIdx].indexOf(letter);
        if (idx > -1) {
          selectedAnswers[qIdx].splice(idx, 1);
        } else if (selectedAnswers[qIdx].length < required) {
          selectedAnswers[qIdx].push(letter);
        } else {
          selectedAnswers[qIdx].shift();
          selectedAnswers[qIdx].push(letter);
        }
      }
      
      updateOptionStyles(qIdx);
      document.getElementById('submit' + qIdx).disabled = selectedAnswers[qIdx].length !== required;
    }

    function updateOptionStyles(qIdx) {
      var optionsContainer = document.getElementById('options' + qIdx);
      if (!optionsContainer) return;
      var options = optionsContainer.querySelectorAll('.option');
      options.forEach(function(opt) {
        var letter = opt.dataset.letter;
        if (selectedAnswers[qIdx].includes(letter)) {
          opt.classList.add('selected');
        } else {
          opt.classList.remove('selected');
        }
      });
    }

    function submitAnswer(qIdx) {
      if (answered[qIdx]) return;
      
      var q = questions[qIdx];
      answered[qIdx] = true;
      
      var selected = selectedAnswers[qIdx];
      var correct = q.correctAnswers || [];
      var isCorrect = q.isHotspot || (
        selected.length === correct.length && 
        selected.every(function(s) { return correct.includes(s); })
      );
      
      if (isCorrect && !q.isHotspot) score++;
      
      // Update option styles - mark as disabled and show correct/incorrect
      var optionsContainer = document.getElementById('options' + qIdx);
      if (optionsContainer) {
        var options = optionsContainer.querySelectorAll('.option');
        options.forEach(function(opt) {
          var letter = opt.dataset.letter;
          opt.classList.remove('selected');
          opt.classList.add('disabled');
          if (correct.includes(letter)) {
            opt.classList.add('correct');
          } else if (selected.includes(letter)) {
            opt.classList.add('incorrect');
          }
        });
      }
      
      // Build result HTML
      var resultDiv = document.getElementById('result' + qIdx);
      resultDiv.className = 'result ' + (isCorrect ? 'correct-result' : 'incorrect-result');
      
      var resultHtml = '<div class="result-status ' + (isCorrect ? 'correct' : 'incorrect') + '">' +
        (q.isHotspot ? '📋 Answer Revealed' : (isCorrect ? '✅ Correct!' : '❌ Incorrect')) + '</div>';
      
      if (q.isHotspot && q.answerImage) {
        resultHtml += '<img src="' + q.answerImage + '" class="hotspot-image" alt="Answer">';
      } else {
        resultHtml += '<div class="correct-answer"><strong>Correct Answer' + (correct.length > 1 ? 's' : '') + ':</strong> ' + correct.join(', ') + '</div>';
      }
      
      // Add explanation
      if (q.explanation) {
        resultHtml += '<div class="explanation"><h4>📖 Explanation:</h4><p>' + escapeHtml(q.explanation) + '</p></div>';
      }
      
      // Add option-by-option rationales if available
      if (q.perOptionRationales && Object.keys(q.perOptionRationales).length > 0) {
        resultHtml += '<div class="per-option-rationales"><h4>\u2696\ufe0f Why each option is right or wrong:</h4><ul>';
        (q.options || []).forEach(function(opt) {
          var r = q.perOptionRationales[opt.letter];
          if (!r) return;
          var right = (q.correctAnswers || []).indexOf(opt.letter) !== -1;
          resultHtml += '<li class="' + (right ? 'rationale-correct' : 'rationale-incorrect') + '">' +
            '<strong>' + (right ? '\u2705' : '\u274c') + ' ' + escapeHtml(opt.letter) + '.</strong> ' +
            escapeHtml(r) + '</li>';
        });
        resultHtml += '</ul></div>';
      }

      // Add key concepts if available
      if (q.concepts && q.concepts.length > 0) {
        resultHtml += '<div class="concepts"><h4>🔑 Key Concepts:</h4><ul>';
        q.concepts.forEach(function(c) {
          resultHtml += '<li>' + escapeHtml(c) + '</li>';
        });
        resultHtml += '</ul></div>';
      }
      
      // Add AWS documentation link - use specific URL if available, fall back to search
      var docsUrl = q.documentationUrl || 'https://docs.aws.amazon.com/';
      var docsLabel = q.documentationLabel || 'AWS Documentation';
      if (!q.documentationUrl && q.concepts && q.concepts.length > 0) {
        docsUrl = 'https://docs.aws.amazon.com/search/doc-search.html?searchPath=documentation&searchQuery=' + encodeURIComponent(q.concepts[0]);
      }
      resultHtml += '<a href="' + docsUrl + '" target="_blank" class="aws-link">📚 ' + escapeHtml(docsLabel) + '</a>';
      
      resultDiv.innerHTML = resultHtml;
      
      // Hide submit, show next
      document.getElementById('submit' + qIdx).classList.add('hidden');
      document.getElementById('next' + qIdx).classList.remove('hidden');
      
      // Update score display
      document.getElementById('correctCount').textContent = score;
    }

    function nextQuestion(qIdx) {
      var currentCard = document.getElementById('q' + qIdx);
      currentCard.classList.add('hidden');
      
      if (qIdx < totalQuestions - 1) {
        currentQuestion = qIdx + 1;
        document.getElementById('q' + currentQuestion).classList.remove('hidden');
        document.getElementById('currentQ').textContent = currentQuestion + 1;
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } else {
        document.getElementById('scoreBar').classList.add('hidden');
        var finalScoreDiv = document.getElementById('finalScore');
        finalScoreDiv.classList.remove('hidden');
        document.getElementById('finalScoreNum').textContent = score + '/' + totalQuestions;
        document.getElementById('finalPercentage').textContent = Math.round((score / totalQuestions) * 100) + '%';
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    }

    function restartQuiz() {
      currentQuestion = 0;
      score = 0;
      document.getElementById('correctCount').textContent = '0';
      document.getElementById('currentQ').textContent = '1';
      document.getElementById('scoreBar').classList.remove('hidden');
      document.getElementById('finalScore').classList.add('hidden');
      init();
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }

    // Initialize on page load
    init();
  </script>

  <footer style="margin-top:40px;padding:20px;text-align:center;font-size:0.78rem;color:#999;border-top:1px solid #e0e0e0;line-height:1.6;">
    This practice quiz is an independent study resource and is <strong>not affiliated with, endorsed by, or associated with Amazon Web Services (AWS)</strong> or Amazon.com, Inc. in any way. All AWS certification names and trademarks are the property of Amazon.com, Inc. or its affiliates. Content is provided for educational purposes only.
  </footer>
</body>
</html>`;
  };

  const fetchAllScores = async () => {
    try {
      const token = await authGetToken();
      const res = await fetch(`${API_URL}/api/admin/scores`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setAllScores(data);
      }
    } catch (err) {
      console.error('Error fetching scores:', err);
    }
  };

  const fetchAllUsers = async () => {
    setUsersLoading(true);
    try {
      const token = await authGetToken();
      const res = await fetch(`${API_URL}/api/admin/users`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setAllUsers(data.users || []);
      } else {
        console.error('Error fetching users:', res.status, await res.text());
      }
    } catch (err) {
      console.error('Error fetching users:', err);
    }
    setUsersLoading(false);
  };

  // Toggle a single cert in a user's blocked list. Optimistic update + server sync.
  const toggleUserCertAccess = async (userId, certId) => {
    const target = allUsers.find(u => u.userId === userId);
    if (!target) return;
    const isBlocked = (target.blockedCerts || []).includes(certId);
    const nextBlocked = isBlocked
      ? target.blockedCerts.filter(id => id !== certId)
      : [...(target.blockedCerts || []), certId];

    // Optimistic UI update
    setAllUsers(prev => prev.map(u =>
      u.userId === userId ? { ...u, blockedCerts: nextBlocked } : u
    ));
    setSavingUserId(userId);

    try {
      const token = await authGetToken();
      const res = await fetch(`${API_URL}/api/admin/users/${encodeURIComponent(userId)}/cert-access`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ blockedCerts: nextBlocked }),
      });
      if (!res.ok) {
        // Roll back on failure
        setAllUsers(prev => prev.map(u =>
          u.userId === userId ? { ...u, blockedCerts: target.blockedCerts || [] } : u
        ));
        console.error('Failed to update cert access:', await res.text());
      }
    } catch (err) {
      setAllUsers(prev => prev.map(u =>
        u.userId === userId ? { ...u, blockedCerts: target.blockedCerts || [] } : u
      ));
      console.error('Error updating cert access:', err);
    }
    setSavingUserId(null);
  };

  const fetchAdminScoreDetail = async (quizId, userId) => {
    try {
      const token = await authGetToken();
      const res = await fetch(`${API_URL}/api/admin/scores/${quizId}?userId=${encodeURIComponent(userId)}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setAdminScoreDetail(data);
      }
    } catch (err) {
      console.error('Error fetching score detail:', err);
    }
  };

  const exportScoresToCsv = () => {
    const headers = ['User Email', 'User ID', 'Exam', 'Score', 'Total', 'Accuracy %', 'Date Taken'];
    const rows = allScores.map(s => [
      s.userEmail || '',
      s.userId,
      s.certId || 'ans-c01',
      s.score?.correct ?? 0,
      s.score?.total ?? 0,
      s.score?.total ? Math.round((s.score.correct / s.score.total) * 100) : 0,
      s.startedAt ? new Date(s.startedAt).toLocaleDateString() : '',
    ]);
    const csv = [headers, ...rows].map(r => r.map(c => `"${c}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `exam-scores-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleSignOut = async () => {
    await authSignOut();
    setUser(null);
    setQuizHistory([]);
    setQuizStarted(false);
    setSelectedCertification(null);
  };

  // Show auth screen if Cognito is configured and user is not signed in
  if (isAuthConfigured() && !authChecked) {
    return <div className="App"><div className="loading">Loading...</div></div>;
  }
  if (isAuthConfigured() && !user) {
    // Visitors who arrive via a deep link (SSO token, shared exam/quiz link)
    // skip the marketing landing and go straight to auth so we don't
    // interrupt an intended action.
    const _params = new URLSearchParams(window.location.search);
    const arrivedViaDeepLink =
      !!prefilledAuthEmail ||
      _params.has('token') || _params.has('exam') || _params.has('quiz');

    if (!showAuth && !arrivedViaDeepLink) {
      return (
        <LandingPage
          certifications={AWS_CERTIFICATIONS}
          onGetStarted={() => { setAuthInitialMode('signUp'); setShowAuth(true); }}
          onSignIn={() => { setAuthInitialMode('signIn'); setShowAuth(true); }}
        />
      );
    }

    return <AuthScreen
      onAuthenticated={() => authGetCurrentUser().then(u => setUser(u))}
      prefilledEmail={prefilledAuthEmail}
      onBack={() => setShowAuth(false)}
      initialMode={authInitialMode}
    />;
  }

  // Stripe redirect post-checkout — render the celebratory splash instead
  // of the cert-selection screen until the user clicks "Start practicing".
  // BillingSuccess polls /billing/my-subscription on its own and shows the
  // appropriate state (activating / active / pending).
  if (showBillingSuccess) {
    return (
      <div className="App">
        <BillingSuccess onContinue={dismissBillingSuccess} />
      </div>
    );
  }

  // isExamLink state drives exam vs quiz mode (set in handleCertificationSelect and URL effect)

  return (
    <div className="App">

      {/* Session-expired notice — non-blocking. Progress is saved on this
          device; signing in again re-syncs it to the server. */}
      {sessionExpired && (
        <div className="session-expired-banner">
          <span>
            ⚠️ Your session expired. Your progress is saved on this device — sign in again to sync it.
          </span>
          <span className="session-expired-actions">
            <button
              className="session-expired-signin"
              onClick={() => window.location.reload()}
            >
              Sign in again
            </button>
            <button
              className="session-expired-dismiss"
              onClick={() => setSessionExpired(false)}
              aria-label="Dismiss"
            >
              ✕
            </button>
          </span>
        </div>
      )}

      {/* Time expired overlay */}
      {/* Exam Results Modal */}
      {showExamResults && examResults && (
        <div className="exam-results-overlay">
          <div className="exam-results-modal">
            {examResults.isQuiz ? (
              <div className="exam-results-verdict quiz-complete">
                <div className="exam-verdict-icon">🎉</div>
                <h1 className="exam-verdict-text">Quiz Complete</h1>
                <p className="exam-verdict-sub">
                  You scored {examResults.percentage}% ({examResults.correct}/{examResults.total} correct).
                </p>
              </div>
            ) : (
              <div className={`exam-results-verdict ${examResults.passed ? 'passed' : 'failed'}`}>
                <div className="exam-verdict-icon">{examResults.passed ? '🏆' : '📚'}</div>
                <h1 className="exam-verdict-text">{examResults.passed ? 'PASS' : 'FAIL'}</h1>
                <p className="exam-verdict-sub">
                  {examResults.passed
                    ? 'Congratulations! You passed the exam.'
                    : 'Keep studying — you\'ll get there!'}
                </p>
              </div>
            )}

            <div className="exam-results-stats">
              <div className="exam-stat">
                <span className="exam-stat-label">Score</span>
                <span className="exam-stat-value">{examResults.correct}/{examResults.total}</span>
              </div>
              <div className="exam-stat">
                <span className="exam-stat-label">Percentage</span>
                <span className={`exam-stat-value ${examResults.isQuiz ? '' : (examResults.passed ? 'stat-pass' : 'stat-fail')}`}>
                  {examResults.percentage}%
                </span>
              </div>
              {!examResults.isQuiz && (
                <div className="exam-stat">
                  <span className="exam-stat-label">Passing</span>
                  <span className="exam-stat-value">85%</span>
                </div>
              )}
              {examResults.timeTakenSeconds && (
                <div className="exam-stat">
                  <span className="exam-stat-label">Time Taken</span>
                  <span className="exam-stat-value">{formatExamTime(examResults.timeTakenSeconds)}</span>
                </div>
              )}
            </div>

            <div className="exam-results-breakdown">
              <h3>Question Breakdown</h3>
              <div className="exam-breakdown-bars">
                <div className="breakdown-bar-row">
                  <span className="breakdown-label correct-label">✅ Correct</span>
                  <div className="breakdown-bar-track">
                    <div
                      className="breakdown-bar correct-bar"
                      style={{ width: `${examResults.total > 0 ? (examResults.correct / examResults.total) * 100 : 0}%` }}
                    />
                  </div>
                  <span className="breakdown-count">{examResults.correct}</span>
                </div>
                <div className="breakdown-bar-row">
                  <span className="breakdown-label incorrect-label">❌ Incorrect</span>
                  <div className="breakdown-bar-track">
                    <div
                      className="breakdown-bar incorrect-bar"
                      style={{ width: `${examResults.total > 0 ? ((examResults.total - examResults.correct) / examResults.total) * 100 : 0}%` }}
                    />
                  </div>
                  <span className="breakdown-count">{examResults.total - examResults.correct}</span>
                </div>
              </div>
            </div>

            <div className="exam-results-actions">
              <button
                className="exam-review-btn"
                onClick={enterReviewMode}
              >
                🔍 Review All Answers
              </button>
              <button
                className="exam-retake-btn"
                onClick={() => { setShowExamResults(false); setReviewMode(false); goBackToCertSelection(); }}
              >
                {examResults.isQuiz ? '🔄 Back to Quizzes' : '🔄 Back to Exams'}
              </button>
            </div>

            {/* Cross-sell — high-intent moment, user just finished an exam */}
            {!examResults.isQuiz && (
              <LearningPlatformPromo
                variant="modal"
                hasExamSub={hasExamSub}
                tierSource={tierSource}
                onOpenPortal={handleOpenExamPortal}
              />
            )}
          </div>
        </div>
      )}

      {/* Timer paused overlay */}
      {examTimerPaused && examTimeLeft !== null && !examTimerExpired && (
        <div className="exam-paused-overlay" onClick={() => { setExamTimerPaused(false); setExamLeftWarning(false); }}>
          <div className="exam-paused-modal" onClick={e => e.stopPropagation()}>
            <div className="exam-paused-icon">{examLeftWarning ? '⚠️' : '⏸'}</div>
            <h2>{examLeftWarning ? 'You Left the Exam!' : 'Exam Paused'}</h2>
            {examLeftWarning && (
              <p className="exam-left-warning-text">
                Leaving the exam screen is not allowed. Your exam has been paused. Please return and resume to continue.
              </p>
            )}
            <p>Time remaining: <strong>{formatExamTime(examTimeLeft)}</strong></p>
            <button
              className="exam-resume-btn"
              onClick={() => { setExamTimerPaused(false); setExamLeftWarning(false); }}
            >
              ▶ Resume Exam
            </button>
          </div>
        </div>
      )}

      {/* Exam disclaimer modal */}
      {showExamDisclaimer && selectedCertification && (
        <div className="exam-disclaimer-overlay">
          <div className="exam-disclaimer-modal">
            <div className="exam-disclaimer-icon">📋</div>
            <h2>Before You Begin</h2>
            <h3>{selectedCertification.name}</h3>

            <div className="exam-disclaimer-details">
              <div className="exam-detail-row">
                <span className="exam-detail-label">📝 Questions</span>
                <span className="exam-detail-value">{selectedCertification.examQuestions || 65}</span>
              </div>
              <div className="exam-detail-row">
                <span className="exam-detail-label">⏱ Time Limit</span>
                <span className="exam-detail-value">{selectedCertification.examMinutes || 120} minutes</span>
              </div>
              <div className="exam-detail-row">
                <span className="exam-detail-label">🎯 Passing Score</span>
                <span className="exam-detail-value">700 / 1000</span>
              </div>
            </div>

            <div className="exam-disclaimer-warning">
              <strong>⚠️ Important:</strong>
              <ul>
                <li>Once the exam starts, the timer cannot be stopped (only paused).</li>
                <li>Switching tabs, minimizing, or leaving the browser will auto-pause your exam and be flagged.</li>
                <li>Closing the browser will trigger a warning — your progress is auto-saved.</li>
              </ul>
            </div>

            <div className="exam-disclaimer-actions">
              <button
                className="exam-begin-btn"
                onClick={confirmStartExam}
              >
                ✅ I Understand — Begin Exam
              </button>
              <button
                className="exam-cancel-btn"
                onClick={() => setShowExamDisclaimer(false)}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Quiz link "Before You Begin" screen */}
      {showQuizDisclaimer && selectedCertification && (
        <div className="exam-disclaimer-overlay">
          <div className="exam-disclaimer-modal">
            <div className="exam-disclaimer-icon">🎯</div>
            <h2>Before You Begin</h2>
            <h3>{selectedCertification.name}</h3>

            <div className="exam-disclaimer-details">
              <div className="exam-detail-row">
                <span className="exam-detail-label">📝 Questions</span>
                <span className="exam-detail-value">15</span>
              </div>
              <div className="exam-detail-row">
                <span className="exam-detail-label">⏱ Time Limit</span>
                <span className="exam-detail-value">No limit</span>
              </div>
              <div className="exam-detail-row">
                <span className="exam-detail-label">📊 Mode</span>
                <span className="exam-detail-value">Practice Quiz</span>
              </div>
            </div>

            <div className="exam-disclaimer-warning">
              <strong>ℹ️ How it works:</strong>
              <ul>
                <li>15 randomly selected questions from the full question bank.</li>
                <li>Each answer is checked immediately with a full explanation.</li>
                <li>No timer — take your time and learn at your own pace.</li>
                <li>Your results are saved to your history automatically.</li>
              </ul>
            </div>

            {/* Course-certificate notice — only relevant to users who arrived
                via a Learning Platform subscription (Free / Student / Pro on
                dreamandbuildcloudai.com). Exam-only subscribers and free
                users without a platform account don't have a course
                certificate path, so this notice is irrelevant to them. */}
            {tierSource === 'platform' && (
              <div className="exam-disclaimer-warning" style={{ borderColor: 'rgba(240,90,40,0.45)', background: 'rgba(240,90,40,0.08)' }}>
                <strong style={{ color: '#fcd9c8' }}>📌 Course certificate requirement:</strong>
                <p style={{ margin: '6px 0 0', color: '#fcd9c8', fontSize: '0.85rem', lineHeight: 1.5 }}>
                  This untimed practice quiz is for learning and is <strong>not counted toward your Dream&amp;Build Cloud course certificate</strong>. To earn the certificate, you must take the timed quiz and timed exam from inside the corresponding course on the main platform.
                </p>
              </div>
            )}

            <div className="exam-disclaimer-actions">
              <button
                className="exam-begin-btn"
                onClick={confirmStartQuiz}
              >
                🎯 Start Quiz
              </button>
              <button
                className="exam-cancel-btn"
                onClick={() => setShowQuizDisclaimer(false)}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Quit exam confirmation */}
      {showQuitConfirm && (
        <div className="exam-quit-overlay">
          <div className="exam-quit-modal">
            <div className="exam-quit-icon">⚠️</div>
            <h2>Quit Exam?</h2>
            <p>Are you sure you want to end the exam early?</p>
            <ul className="exam-quit-consequences">
              <li>All <strong>{questions.length - Object.keys(answeredQuestions).length}</strong> unanswered question{questions.length - Object.keys(answeredQuestions).length !== 1 ? 's' : ''} will be marked <strong>incorrect</strong>.</li>
              <li>Your result will be graded and saved.</li>
              <li>This cannot be undone.</li>
            </ul>
            <div className="exam-quit-actions">
              <button className="exam-quit-confirm-btn" onClick={quitExam}>
                Yes, End Exam
              </button>
              <button
                className="exam-quit-cancel-btn"
                onClick={() => { setShowQuitConfirm(false); setExamTimerPaused(false); }}
              >
                Continue Exam
              </button>
            </div>
          </div>
        </div>
      )}
      {!selectedCertification ? (
        // Certification Selection Screen
        <div className="cert-selection-screen">
          <header className="App-header">
            <div className="cert-hero">
              <div className="cert-hero-left">
                <h1>AWS Certification Study Hub</h1>
                <p className="cert-hero-sub">
                  {AWS_CERTIFICATIONS.filter(c => c.available && !hiddenCerts.includes(c.id) && !myBlockedCerts.includes(c.id) || isAdmin && c.available).length} exams available · Practice quizzes, timed exams &amp; detailed explanations
                </p>
              </div>
              <div className="cert-hero-right">
                <LearningPlatformPromo variant="pill" tierSource={tierSource} />
                {isAuthConfigured() && user && (
                  <span className="user-email">
                    {user.signInDetails?.loginId || user.username}
                  </span>
                )}
                {isAdmin && (
                  <button
                    className="admin-settings-toggle"
                    onClick={() => setSettingsPanelOpen(!settingsPanelOpen)}
                    title="Admin Settings"
                  >
                    ⚙️ Admin
                  </button>
                )}
                {isAuthConfigured() && user && (
                  <button className="sign-out-btn" onClick={handleSignOut}>Sign Out</button>
                )}
              </div>
            </div>
          </header>

          {/* Admin Settings Side Panel */}
          {isAdmin && (
            <>
              <div className={`admin-settings-panel ${settingsPanelOpen ? 'open' : ''}`}>
                <div className="admin-settings-header">
                  <h2>⚙️ Admin Settings</h2>
                  <button 
                    className="close-panel"
                    onClick={() => setSettingsPanelOpen(false)}
                  >
                    ✕
                  </button>
                </div>
                
                <div className="admin-settings-content">
                  <div className="admin-section">
                    <h3>Exam Visibility</h3>
                    <p className="admin-section-hint">Unchecked exams are hidden for non-admin users.</p>
                    
                    {['Foundational', 'Associate', 'Professional', 'Specialty'].map(level => {
                      const levelCerts = AWS_CERTIFICATIONS.filter(c => c.level === level);
                      if (levelCerts.length === 0) return null;
                      return (
                        <div key={level} className="admin-cert-level-group">
                          <h4 className="admin-cert-level-title">{level}</h4>
                          <div className="admin-cert-toggles">
                            {levelCerts.map(cert => (
                              <label key={cert.id} className="admin-cert-toggle">
                                <input
                                  type="checkbox"
                                  checked={!hiddenCerts.includes(cert.id)}
                                  onChange={() => toggleCertVisibility(cert.id)}
                                />
                                <span className="cert-toggle-label">
                                  <span className="cert-toggle-code">{cert.code}</span>
                                  <span className="cert-toggle-name">{cert.name}</span>
                                </span>
                              </label>
                            ))}
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  <div className="admin-section">
                    <h3>Download Quiz Settings</h3>
                    <p className="admin-section-hint">Number of questions in downloaded HTML quizzes.</p>
                    <div className="admin-quiz-count">
                      <label htmlFor="quizCountInput">Questions per quiz:</label>
                      <input
                        id="quizCountInput"
                        type="number"
                        min="10"
                        max="50"
                        value={quizDownloadCount}
                        onChange={(e) => {
                          const val = Math.min(50, Math.max(10, parseInt(e.target.value) || 15));
                          setQuizDownloadCount(val);
                        }}
                        className="quiz-count-input"
                      />
                      <span className="quiz-count-range">(10 – 50)</span>
                    </div>
                  </div>

                  <div className="admin-section">
                    <h3>User Management</h3>
                    <button 
                      className="admin-action-btn" 
                      onClick={() => { setAdminView('scores'); fetchAllScores(); setSettingsPanelOpen(false); }}
                    >
                      📊 View All User Scores
                    </button>
                    <button 
                      className="admin-action-btn" 
                      onClick={() => { setAdminView('users'); fetchAllUsers(); setSettingsPanelOpen(false); }}
                      style={{ marginTop: 8 }}
                    >
                      👥 Manage User Cert Access
                    </button>
                  </div>
                </div>
              </div>
              {settingsPanelOpen && (
                <div 
                  className="overlay"
                  onClick={() => setSettingsPanelOpen(false)}
                />
              )}
            </>
          )}

          {adminView === 'scores' && isAdmin && (
            <div className="admin-scores-overlay">
              <div className="admin-scores-panel">
                <div className="admin-scores-header">
                  <h2>📊 All User Results</h2>
                  <div className="admin-scores-actions">
                    <div className="admin-filter-tabs">
                      {['all', 'exam', 'quiz'].map(f => (
                        <button
                          key={f}
                          className={`admin-filter-tab ${adminScoreFilter === f ? 'active' : ''}`}
                          onClick={() => setAdminScoreFilter(f)}
                        >
                          {f === 'all' ? 'All' : f === 'exam' ? '🎓 Exams' : '🎯 Quizzes'}
                        </button>
                      ))}
                    </div>
                    <button className="admin-btn" onClick={exportScoresToCsv} disabled={allScores.length === 0}>
                      📥 Export CSV
                    </button>
                    <button className="close-panel" onClick={() => { setAdminView(null); setAdminScoreDetail(null); }}>✕</button>
                  </div>
                </div>

                {/* Drill-down detail view */}
                {adminScoreDetail ? (
                  <div className="admin-detail-view">
                    <div className="admin-detail-header">
                      <button className="admin-back-btn" onClick={() => setAdminScoreDetail(null)}>← Back to List</button>
                      <div className="admin-detail-meta">
                        <span><strong>User:</strong> {adminScoreDetail.userEmail || adminScoreDetail.userId}</span>
                        <span><strong>Exam:</strong> {(adminScoreDetail.certId || '').toUpperCase()}</span>
                        <span><strong>Date:</strong> {adminScoreDetail.startedAt ? new Date(adminScoreDetail.startedAt).toLocaleString() : '—'}</span>
                        {adminScoreDetail.examResult && (
                          <span className={`admin-detail-verdict ${adminScoreDetail.examResult.passed ? 'badge-pass' : 'badge-fail'}`}>
                            {adminScoreDetail.examResult.passed ? '✅ PASS' : '❌ FAIL'} — {adminScoreDetail.examResult.percentage}%
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="admin-detail-questions">
                      {(adminScoreDetail.questions || []).map((q, idx) => {
                        const answered = adminScoreDetail.answeredQuestions?.[idx];
                        const isCorrect = answered?.result?.isCorrect;
                        const wasAnswered = !!answered;
                        return (
                          <div key={idx} className={`admin-q-row ${!wasAnswered ? 'unanswered' : isCorrect ? 'q-correct' : 'q-incorrect'}`}>
                            <div className="admin-q-header">
                              <span className="admin-q-num">Q{idx + 1}</span>
                              <span className={`admin-q-status ${!wasAnswered ? 'status-skip' : isCorrect ? 'status-correct' : 'status-wrong'}`}>
                                {!wasAnswered ? '—' : isCorrect ? '✅' : '❌'}
                              </span>
                            </div>
                            <p className="admin-q-text">{q.question}</p>
                            {wasAnswered && (
                              <div className="admin-q-answers">
                                <span className="admin-q-selected">
                                  Selected: <strong>{answered.selectedAnswers?.join(', ') || '—'}</strong>
                                </span>
                                {!isCorrect && (
                                  <span className="admin-q-correct-ans">
                                    Correct: <strong>{answered.result?.correctAnswers?.join(', ') || '—'}</strong>
                                  </span>
                                )}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ) : (
                  <>
                    {allScores.filter(s =>
                      adminScoreFilter === 'all' ? true :
                      adminScoreFilter === 'exam' ? s.isExamMode :
                      !s.isExamMode
                    ).length === 0 ? (
                      <p className="admin-no-data">No results found.</p>
                    ) : (
                      <div className="admin-scores-table-wrap">
                        <table className="admin-scores-table">
                          <thead>
                            <tr>
                              <th>User</th>
                              <th>Exam</th>
                              <th>Type</th>
                              <th>Score</th>
                              <th>Result</th>
                              <th>Date</th>
                              <th></th>
                            </tr>
                          </thead>
                          <tbody>
                            {allScores
                              .filter(s =>
                                adminScoreFilter === 'all' ? true :
                                adminScoreFilter === 'exam' ? s.isExamMode :
                                !s.isExamMode
                              )
                              .map((s, i) => {
                                const pct = s.score?.total ? Math.round((s.score.correct / s.score.total) * 100) : 0;
                                return (
                                  <tr key={i}>
                                    <td title={s.userId}>{s.userEmail || s.userId?.slice(0, 8) + '...'}</td>
                                    <td>{(s.certId || 'ans-c01').toUpperCase()}</td>
                                    <td>
                                      <span className={`type-badge ${s.isExamMode ? 'type-exam' : 'type-quiz'}`}>
                                        {s.isExamMode ? '🎓 Exam' : '🎯 Quiz'}
                                      </span>
                                    </td>
                                    <td>{s.score?.correct ?? 0}/{s.score?.total ?? 0} ({pct}%)</td>
                                    <td>
                                      {s.isExamMode && s.examResult ? (
                                        <span className={`result-badge ${s.examResult.passed ? 'badge-pass' : 'badge-fail'}`}>
                                          {s.examResult.passed ? '✅ PASS' : '❌ FAIL'}
                                        </span>
                                      ) : (
                                        <span className="result-badge badge-neutral">—</span>
                                      )}
                                    </td>
                                    <td>{s.startedAt ? new Date(s.startedAt).toLocaleDateString() : '—'}</td>
                                    <td>
                                      <button
                                        className="admin-detail-btn"
                                        onClick={() => fetchAdminScoreDetail(s.quizId, s.userId)}
                                      >
                                        View
                                      </button>
                                    </td>
                                  </tr>
                                );
                              })}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          )}

          {adminView === 'users' && isAdmin && (
            <div className="admin-scores-overlay">
              <div className="admin-scores-panel admin-users-panel">
                <div className="admin-scores-header">
                  <h2>👥 User Cert Access</h2>
                  <div className="admin-scores-actions">
                    <input
                      type="search"
                      placeholder="Search by email…"
                      value={userSearch}
                      onChange={(e) => setUserSearch(e.target.value)}
                      className="admin-user-search"
                    />
                    <button className="admin-btn" onClick={fetchAllUsers} disabled={usersLoading}>
                      {usersLoading ? 'Refreshing…' : '↻ Refresh'}
                    </button>
                    <button className="close-panel" onClick={() => setAdminView(null)}>✕</button>
                  </div>
                </div>

                {usersLoading ? (
                  <p className="admin-no-data">Loading users…</p>
                ) : allUsers.length === 0 ? (
                  <p className="admin-no-data">No users found.</p>
                ) : (
                  <div className="admin-users-table-wrap">
                    <p className="admin-section-hint">
                      Check a cert to <strong>grant</strong> access; uncheck to <strong>block</strong> it for that user.
                      Admins always see all certs.
                    </p>
                    <table className="admin-users-table">
                      <thead>
                        <tr>
                          <th className="user-col">User</th>
                          <th className="status-col">Status</th>
                          {AWS_CERTIFICATIONS.filter(c => c.available).map(c => (
                            <th key={c.id} className="cert-col" title={c.name}>{c.code}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {allUsers
                          .filter(u => !userSearch || (u.email || '').toLowerCase().includes(userSearch.toLowerCase()))
                          .map(u => (
                            <tr key={u.userId} className={savingUserId === u.userId ? 'saving' : ''}>
                              <td className="user-col">
                                <div className="user-cell">
                                  <span className="user-email">{u.email || u.username}</span>
                                  <span className="user-meta">
                                    {u.createdAt ? `Joined ${new Date(u.createdAt).toLocaleDateString()}` : '—'}
                                  </span>
                                </div>
                              </td>
                              <td className="status-col">
                                <span className={`user-status status-${(u.status || '').toLowerCase()}`}>
                                  {u.status === 'CONFIRMED' ? '✅ Confirmed' :
                                   u.status === 'FORCE_CHANGE_PASSWORD' ? '🔑 Pwd Reset' :
                                   u.status === 'UNCONFIRMED' ? '⏳ Unconfirmed' :
                                   u.status || '—'}
                                </span>
                              </td>
                              {AWS_CERTIFICATIONS.filter(c => c.available).map(c => {
                                const blocked = (u.blockedCerts || []).includes(c.id);
                                return (
                                  <td key={c.id} className="cert-col">
                                    <input
                                      type="checkbox"
                                      checked={!blocked}
                                      onChange={() => toggleUserCertAccess(u.userId, c.id)}
                                      disabled={savingUserId === u.userId}
                                      aria-label={`${blocked ? 'Grant' : 'Block'} ${c.code} for ${u.email}`}
                                    />
                                  </td>
                                );
                              })}
                            </tr>
                          ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          )}

          <div className="cert-grid">
            {/* Cross-sell to D&B Cloud Learning Platform — appears above
                the level tabs so it's the first thing in the cert grid. */}
            <LearningPlatformPromo
              variant="wide"
              hasExamSub={hasExamSub}
              tierSource={tierSource}
              onOpenPortal={handleOpenExamPortal}
            />
            {/* Level filter tabs */}
            <div className="cert-level-tabs">
              {['Foundational', 'Associate', 'Professional', 'Specialty'].map(level => {
                const count = AWS_CERTIFICATIONS.filter(c =>
                  c.level === level && c.available &&
                  (isAdmin || (!hiddenCerts.includes(c.id) && !myBlockedCerts.includes(c.id)))
                ).length;
                return (
                  <button
                    key={level}
                    className={`cert-level-tab ${certLevelFilter === level ? 'active' : ''}`}
                    onClick={() => setCertLevelFilter(level)}
                  >
                    {level}
                    {count > 0 && <span className="cert-level-tab-count">{count}</span>}
                  </button>
                );
              })}
            </div>

            {/* Active level cards */}
            {(() => {
              const visibleCerts = AWS_CERTIFICATIONS.filter(c =>
                c.level === certLevelFilter &&
                (isAdmin || (!hiddenCerts.includes(c.id) && !myBlockedCerts.includes(c.id)))
              );
              const available = visibleCerts.filter(c => c.available);
              const comingSoon = visibleCerts.filter(c => !c.available);

              return (
                <div className="cert-level-panel">
                  {available.length === 0 && comingSoon.length === 0 && (
                    <p className="cert-empty">No certifications in this category yet.</p>
                  )}

                  {available.length > 0 && (
                    <div className="cert-cards">
                      {available.map(cert => {
                        const certAccess = hasAccessToCert(userTier, cert.code, userTierEmail, accessOpts);
                        const family = certFamily(cert.code);
                        const tierBadge = certAccess ? null : certTierBadge(cert.code);
                        return (
                        <div
                          key={cert.id}
                          className={`cert-card available fam-${family} ${certAccess ? 'cert-card-unlocked' : 'cert-card-locked'} ${cert.retiring ? 'retiring' : ''}`}
                          onClick={() => handleCertificationSelect(cert)}
                          role="button"
                          tabIndex={0}
                          onKeyDown={(e) => {
                            // Cards are divs, so wire Enter/Space to match
                            // native button behavior for keyboard users.
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              handleCertificationSelect(cert);
                            }
                          }}
                          aria-label={certAccess
                            ? `Start studying ${cert.code} ${cert.name}`
                            : `${cert.code} ${cert.name} — locked. Preview 10 free questions`}
                        >
                          {/* Family color stripe at the top */}
                          <span className="cert-card-stripe" aria-hidden="true" />

                          <div className="cert-card-content">
                            <div className="cert-card-top">
                              <span className="cert-code">{cert.code}</span>
                              {tierBadge && (
                                <span className={`tier-badge ${tierBadge.cls}`}>{tierBadge.label}</span>
                              )}
                            </div>
                            <h3 className="cert-name">{cert.name}</h3>
                            {cert.examQuestions && (
                              <div className="cert-meta-row">
                                <span className="cert-meta-pill">📝 {cert.examQuestions} questions</span>
                                <span className="cert-meta-pill">⏱ {cert.examMinutes} min exam</span>
                              </div>
                            )}
                            {cert.retiring && <span className="retiring-badge">⚠️ {cert.retiring}</span>}
                            {cert.note && <span className="cert-note">{cert.note}</span>}

                            {/* Bottom CTA group — sits at the card bottom via flex */}
                            <div className="cert-card-cta-group">
                              {certAccess ? (
                                <span className="available-badge">Start Studying →</span>
                              ) : (
                                <>
                                  <UpgradeBlock
                                    requiredTier={tierForCert(cert.code)}
                                    compact
                                    certCode={cert.code}
                                    certName={cert.name}
                                  />
                                  <button
                                    type="button"
                                    className="preview-link"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      openPreviewModal(cert);
                                    }}
                                    aria-label={`Preview 10 free questions for ${cert.code}`}
                                  >
                                    Preview 10 free questions →
                                  </button>
                                </>
                              )}
                            </div>
                          </div>

                          {isAdmin && (
                            <div className="cert-card-actions">
                              <button
                                className="copy-link-btn"
                                onClick={(e) => { e.stopPropagation(); setInviteModalCert({ ...cert, mode: 'exam' }); }}
                                title="Send exam link or copy URL"
                              >
                                🎓 Exam Link
                              </button>
                              <button
                                className="copy-link-btn quiz-link-btn"
                                onClick={(e) => { e.stopPropagation(); setInviteModalCert({ ...cert, mode: 'quiz' }); }}
                                title="Send 15-question quiz link or copy URL"
                              >
                                🎯 Quiz Link
                              </button>
                              <button
                                className="download-quiz-btn"
                                onClick={(e) => { e.stopPropagation(); downloadQuizHtml(cert); }}
                              >
                                📥 Download Quiz
                              </button>
                            </div>
                          )}
                        </div>
                      );})}
                    </div>
                  )}

                  {comingSoon.length > 0 && (
                    <div className="cert-coming-soon-row">
                      <span className="cert-coming-soon-label">Coming soon</span>
                      {comingSoon.map(cert => (
                        <span key={cert.id} className="cert-coming-soon-chip" title={cert.name}>
                          {cert.code}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              );
            })()}
          </div>

          <footer className="site-disclaimer">
            <p>
              This platform is an independent study tool and is <strong>not affiliated with, endorsed by, or associated with Amazon Web Services (AWS)</strong> or Amazon.com, Inc. in any way. All AWS certification names, trademarks, and logos are the property of Amazon.com, Inc. or its affiliates. Practice questions are provided for educational purposes only.
            </p>
          </footer>

          {/* Exam Invite Modal */}
          {inviteModalCert && (
            <ExamInviteModal
              cert={inviteModalCert}
              apiUrl={API_URL}
              getToken={authGetToken}
              onClose={() => setInviteModalCert(null)}
            />
          )}

          {/* Preview Modal — 10 free questions per cert, no auth required */}
          {previewCert && (
            <div
              className="preview-modal-backdrop"
              role="dialog"
              aria-modal="true"
              aria-label={`Preview questions for ${previewCert.code}`}
              onClick={closePreviewModal}
            >
              <div className="preview-modal" onClick={(e) => e.stopPropagation()}>
                <button
                  type="button"
                  className="preview-modal-close"
                  onClick={closePreviewModal}
                  aria-label="Close preview"
                >
                  ✕
                </button>
                <div className="preview-modal-header">
                  <span className="preview-modal-eyebrow">Free preview</span>
                  <h2 className="preview-modal-title">
                    {previewCert.code} — {previewCert.name}
                  </h2>
                  <p className="preview-modal-subtitle">
                    {previewLoading
                      ? 'Loading 10 sample questions…'
                      : previewError
                        ? previewError
                        : previewQuestions.length > 0
                          ? `Question ${previewIndex + 1} of ${previewQuestions.length}`
                          : ''}
                  </p>
                </div>

                {!previewLoading && !previewError && previewQuestions.length > 0 && (() => {
                  const q = previewQuestions[previewIndex];
                  const correctSet = new Set(q.correctAnswers || []);
                  return (
                    <div className="preview-modal-body">
                      <div className="preview-question-text">
                        {q.question}
                      </div>
                      <ul className="preview-options">
                        {(q.options || []).map((opt) => {
                          const isCorrect = correctSet.has(opt.letter);
                          const cls = previewRevealed
                            ? (isCorrect ? 'preview-option correct' : 'preview-option')
                            : 'preview-option';
                          return (
                            <li key={opt.letter} className={cls}>
                              <span className="preview-option-letter">{opt.letter}.</span>{' '}
                              <span className="preview-option-text">{opt.text}</span>
                              {previewRevealed && isCorrect && (
                                <span className="preview-option-badge"> ✓ Correct</span>
                              )}
                            </li>
                          );
                        })}
                      </ul>
                      {previewRevealed && q.explanation && (
                        <div className="preview-explanation">
                          <strong>Explanation:</strong>
                          <p>{q.explanation}</p>
                        </div>
                      )}
                      <div className="preview-controls">
                        {!previewRevealed ? (
                          <button
                            type="button"
                            className="preview-btn preview-btn-primary"
                            onClick={() => setPreviewRevealed(true)}
                          >
                            Show answer
                          </button>
                        ) : (
                          <>
                            <button
                              type="button"
                              className="preview-btn"
                              disabled={previewIndex === 0}
                              onClick={() => {
                                setPreviewIndex((i) => Math.max(0, i - 1));
                                setPreviewRevealed(false);
                              }}
                            >
                              ← Previous
                            </button>
                            {previewIndex < previewQuestions.length - 1 ? (
                              <button
                                type="button"
                                className="preview-btn preview-btn-primary"
                                onClick={() => {
                                  setPreviewIndex((i) => i + 1);
                                  setPreviewRevealed(false);
                                }}
                              >
                                Next question →
                              </button>
                            ) : (
                              <span className="preview-end-note">
                                That's all 10 — like what you see?
                              </span>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                  );
                })()}

                <div className="preview-modal-footer">
                  <p className="preview-modal-pitch">
                    The full {previewCert.code} bank has hundreds of questions, timed
                    exams, scaled scoring, and detailed explanations. Subscribe for{' '}
                    <strong>$14.99/month</strong> to unlock it all.
                  </p>
                  <UpgradeBlock
                    requiredTier={tierForCert(previewCert.code)}
                    certCode={previewCert.code}
                    certName={previewCert.name}
                  />
                </div>
              </div>
            </div>
          )}
        </div>
      ) : (
        // Main Quiz App (existing code)
        <>
          <header className="App-header">
            {reviewMode && (
              <div className="review-mode-banner">
                <span>📋 Review Mode — {examResults?.isQuiz ? 'Quiz Complete' : 'Exam Complete'}</span>
                <div className="review-mode-actions">
                  {examResults && (
                    <span className={`review-verdict-badge ${examResults.passed ? 'badge-pass' : 'badge-fail'}`}>
                      {examResults.passed ? '✅ PASS' : '❌ FAIL'} — {examResults.percentage}%
                    </span>
                  )}
                  <button className="review-back-btn" onClick={() => setShowExamResults(true)}>
                    View Results
                  </button>
                </div>
              </div>
            )}
            <div className="header-with-back">
              <button className="back-to-certs" onClick={goBackToCertSelection}>
                ← All Certifications
              </button>
              <h1>{selectedCertification.name}</h1>
              <div className="header-user-actions">
                {isAuthConfigured() && user && (
                  <span className="user-email">
                    {user.signInDetails?.loginId || user.username}
                  </span>
                )}
                {isAuthConfigured() && user && (
                  <button className="sign-out-btn" onClick={handleSignOut}>Sign Out</button>
                )}
              </div>
            </div>
            {quizStarted && viewMode === 'quiz' && questions.length > 0 && (
              <div className="score">
                <span className="score-text">Score: {score.correct}/{score.total}</span>
                <span className="score-divider">·</span>
                <span className="score-text">Question {currentQuestionIndex + 1} of {questions.length}</span>
                {examTimeLeft !== null && (
                  <span className={`exam-timer ${getTimerClass(examTimeLeft)}`}>
                    ⏱ {formatExamTime(examTimeLeft)}
                    <button
                      className="timer-pause-btn"
                      onClick={() => setExamTimerPaused(p => !p)}
                      title={examTimerPaused ? 'Resume timer' : 'Pause timer'}
                    >
                      {examTimerPaused ? '▶' : '⏸'}
                    </button>
                  </span>
                )}
                {examActive && (
                  <button
                    className="exam-quit-btn"
                    onClick={() => { setExamTimerPaused(true); setShowQuitConfirm(true); }}
                    title="Quit exam"
                  >
                    ✕ Quit Exam
                  </button>
                )}
              </div>
            )}
            {quizStarted && viewMode === 'quiz' && questions.length > 0 && (
              <div className="exam-progress-bar-track">
                <div
                  className="exam-progress-bar-fill"
                  style={{ width: `${((currentQuestionIndex + 1) / questions.length) * 100}%` }}
                />
              </div>
            )}
            {quizStarted && viewMode !== 'quiz' && viewMode !== 'history' && questions.length > 0 && (
              <div className="score">
                Question {currentQuestionIndex + 1} of {questions.length} | Total Available: {questionStats?.total || 0}
              </div>
            )}
          </header>

      {quizStarted && (
        <button 
          className="menu-toggle"
          onClick={() => setSidePanelOpen(!sidePanelOpen)}
          aria-label="Toggle menu"
        >
          ☰ Menu
        </button>
      )}

      <div className={`side-panel ${sidePanelOpen ? 'open' : ''}`}>
        <div className="side-panel-header">
          <h2>Study Modes</h2>
          <button 
            className="close-panel"
            onClick={() => setSidePanelOpen(false)}
            aria-label="Close menu"
          >
            ✕
          </button>
        </div>
        
        <nav className="side-panel-nav">
          <button 
            className={viewMode === 'quiz' ? 'active' : ''}
            onClick={() => handleModeChange('quiz')}
          >
            <span className="icon">🎯</span>
            <span className="label">Quiz Mode</span>
            <span className="description">Random {isExamLink ? (selectedCertification?.examQuestions || 65) : 20} questions</span>
          </button>
          
          <button 
            className={viewMode === 'browse' ? 'active' : ''}
            onClick={() => handleModeChange('browse')}
          >
            <span className="icon">📚</span>
            <span className="label">Browse All</span>
            <span className="description">All {questionStats?.total || 270} questions</span>
          </button>
          
          <button 
            className={viewMode === 'range' ? 'active' : ''}
            onClick={() => {
              setViewMode('range');
              setQuizStarted(true);
            }}
          >
            <span className="icon">🔢</span>
            <span className="label">Question Range</span>
            <span className="description">Custom range</span>
          </button>
          
          <button 
            className={viewMode === 'history' ? 'active' : ''}
            onClick={() => handleModeChange('history')}
          >
            <span className="icon">📜</span>
            <span className="label">Quiz History</span>
            <span className="description">
              {quizHistory.filter(q => (q.certId || 'ans-c01') === (selectedCertification?.id || 'ans-c01')).length} saved quiz{quizHistory.filter(q => (q.certId || 'ans-c01') === (selectedCertification?.id || 'ans-c01')).length !== 1 ? 'zes' : ''}
              {quizHistory.filter(q => (q.certId || 'ans-c01') === (selectedCertification?.id || 'ans-c01') && !q.completed).length > 0 && (
                <span style={{ marginLeft: 6, background: 'rgba(251,191,36,0.15)', border: '1px solid rgba(251,191,36,0.4)', color: '#fbbf24', borderRadius: 4, fontSize: '0.68rem', fontWeight: 700, padding: '1px 6px' }}>
                  ⏸ {quizHistory.filter(q => (q.certId || 'ans-c01') === (selectedCertification?.id || 'ans-c01') && !q.completed).length} in progress
                </span>
              )}
            </span>
          </button>
        </nav>
      </div>

      {sidePanelOpen && (
        <div 
          className="overlay"
          onClick={() => setSidePanelOpen(false)}
        />
      )}

      {showJumpDialog && (
        <>
          <div 
            className="overlay"
            onClick={() => {
              setShowJumpDialog(false);
              setJumpToNumber('');
            }}
          />
          <div className="jump-dialog">
            <div className="jump-dialog-header">
              <h3>Jump to Question</h3>
              <button 
                className="close-dialog"
                onClick={() => {
                  setShowJumpDialog(false);
                  setJumpToNumber('');
                }}
              >
                ✕
              </button>
            </div>
            <form onSubmit={handleJumpToQuestion}>
              <label>
                Enter question number (1-{questionStats?.maxId || 270}):
                <input 
                  type="number"
                  value={jumpToNumber}
                  onChange={(e) => setJumpToNumber(e.target.value)}
                  min="1"
                  max={questionStats?.maxId || 270}
                  placeholder="e.g., 42"
                  autoFocus
                />
              </label>
              <div className="jump-dialog-actions">
                <button type="submit" className="jump-submit-btn">
                  Jump
                </button>
                <button 
                  type="button" 
                  className="jump-cancel-btn"
                  onClick={() => {
                    setShowJumpDialog(false);
                    setJumpToNumber('');
                  }}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </>
      )}

      {!quizStarted ? (
        <div className="welcome-screen">
          <div className="welcome-content">
            <div className="welcome-icon">🎓</div>
            <h2>{selectedCertification.name}</h2>
            <p>
              Start with a <strong>20-question quiz</strong> drawn from {questionStats?.total || '...'} practice questions
              {examUnlocked && ` · full exam: ${selectedCertification?.examQuestions || 65} questions in ${selectedCertification?.examMinutes || 120} min`}
            </p>
            {userTier === 'free' && FREE_TIER_CERTS.has(selectedCertification?.id) && (() => {
              const quizUsed = getAttemptCount(selectedCertification?.id, 'quiz');
              const examUsed = getAttemptCount(selectedCertification?.id, 'exam');
              return (
                <div style={{ display: 'flex', gap: 10, justifyContent: 'center', margin: '8px 0 4px', flexWrap: 'wrap' }}>
                  <span style={{ background: quizUsed >= 1 ? 'rgba(239,68,68,0.12)' : 'rgba(74,222,128,0.1)', border: `1px solid ${quizUsed >= 1 ? 'rgba(239,68,68,0.4)' : 'rgba(74,222,128,0.35)'}`, color: quizUsed >= 1 ? '#f87171' : '#4ade80', borderRadius: 6, fontSize: '0.78rem', fontWeight: 700, padding: '3px 10px' }}>
                    Quiz: {quizUsed >= 1 ? '0 attempts left' : '1 attempt left'}
                  </span>
                  <span style={{ background: examUsed >= 1 ? 'rgba(239,68,68,0.12)' : 'rgba(74,222,128,0.1)', border: `1px solid ${examUsed >= 1 ? 'rgba(239,68,68,0.4)' : 'rgba(74,222,128,0.35)'}`, color: examUsed >= 1 ? '#f87171' : '#4ade80', borderRadius: 6, fontSize: '0.78rem', fontWeight: 700, padding: '3px 10px' }}>
                    Exam: {examUsed >= 1 ? '0 attempts left' : '1 attempt left'}
                  </span>
                </div>
              );
            })()}
            {!hasAccessToCert(userTier, selectedCertification?.code, userTierEmail, accessOpts) ? (
              <UpgradeBlock
                requiredTier={tierForCert(selectedCertification?.code)}
                certCode={selectedCertification?.code}
                certName={selectedCertification?.name}
              />
            ) : (
              <>
                <div className="cta-row">
                  {/* Quiz is the entry point for every cert — always available
                      once the tier check passes. */}
                  <button className="start-quiz-btn" onClick={startQuizMode}>
                    Start Quiz — 20 questions
                  </button>

                  {/* The timed exam unlocks at QUIZ_UNLOCK_PERCENT on the quiz. */}
                  {examUnlocked ? (
                    <button
                      className="start-quiz-btn start-quiz-btn-alt"
                      onClick={() => setShowExamDisclaimer(true)}
                    >
                      Start Timed Exam
                    </button>
                  ) : (
                    <button
                      className="start-quiz-btn start-quiz-btn-alt exam-locked-btn"
                      disabled
                      title={`Score ${QUIZ_UNLOCK_PERCENT}% or higher on the quiz to unlock the timed exam`}
                      aria-label={`Timed exam locked. Score ${QUIZ_UNLOCK_PERCENT}% or higher on the quiz to unlock it.`}
                    >
                      🔒 Timed Exam — locked
                    </button>
                  )}
                </div>

                {/* Explain the gate so it never feels arbitrary. */}
                {examUnlocked ? (
                  <p className="exam-unlock-note unlocked">
                    ✓ Timed exam unlocked — retake it as many times as you like.
                  </p>
                ) : (
                  <p className="exam-unlock-note">
                    Score <strong>{QUIZ_UNLOCK_PERCENT}%+</strong> on the 20-question quiz to unlock the full timed exam.
                    {bestQuizPercent > 0 && (
                      <> Your best so far: <strong>{Math.round(bestQuizPercent)}%</strong>.</>
                    )}
                  </p>
                )}
              </>
            )}
            <div className="welcome-options">
              <p>Or choose a different mode:</p>
              <div className="welcome-buttons">
                <button onClick={() => handleModeChange('browse')}>
                  📚 Browse All Questions
                </button>
                <button onClick={() => {
                  setViewMode('range');
                  setQuizStarted(true);
                }}>
                  🔢 Question Range
                </button>
                {quizHistory.filter(q => (q.certId || 'ans-c01') === (selectedCertification?.id || 'ans-c01')).length > 0 && (
                  <button onClick={() => handleModeChange('history')} style={{ position: 'relative' }}>
                    📜 Quiz History ({quizHistory.filter(q => (q.certId || 'ans-c01') === (selectedCertification?.id || 'ans-c01')).length})
                    {quizHistory.filter(q => (q.certId || 'ans-c01') === (selectedCertification?.id || 'ans-c01') && !q.completed).length > 0 && (
                      <span style={{ marginLeft: 8, background: 'rgba(251,191,36,0.15)', border: '1px solid rgba(251,191,36,0.4)', color: '#fbbf24', borderRadius: 4, fontSize: '0.72rem', fontWeight: 700, padding: '1px 7px', verticalAlign: 'middle' }}>
                        ⏸ {quizHistory.filter(q => (q.certId || 'ans-c01') === (selectedCertification?.id || 'ans-c01') && !q.completed).length} in progress
                      </span>
                    )}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      ) : viewMode === 'range' && (
        <div className="range-selector">
          <form onSubmit={handleRangeSubmit}>
            <label>
              From Question #:
              <input 
                type="number" 
                value={rangeStart} 
                onChange={(e) => setRangeStart(e.target.value)}
                min="1" 
                max={questionStats?.maxId || 270}
                placeholder="1"
              />
            </label>
            <label>
              To Question #:
              <input 
                type="number" 
                value={rangeEnd} 
                onChange={(e) => setRangeEnd(e.target.value)}
                min="1" 
                max={questionStats?.maxId || 270}
                placeholder="50"
              />
            </label>
            <button type="submit">Load Range</button>
          </form>
        </div>
      )}

      {quizStarted && viewMode === 'history' ? (
        <div className="history-container">
          <h2>Quiz History</h2>
          <p className="history-disclaimer">
            📌 Quiz records are automatically removed 45 days after the exam was taken.
          </p>
          {(() => {
              const certHistory = isAuthConfigured()
                ? quizHistory  // API already filters by cert
                : quizHistory.filter(q => (q.certId || 'ans-c01') === (selectedCertification?.id || 'ans-c01'));
              return certHistory.length === 0 ? (
            <div className="no-history">
              <p>No quiz history yet. Complete a quiz to see it here!</p>
            </div>
          ) : (
            <div className="history-list">
              {certHistory.map((quiz) => {
                // Calculate wrong answers count
                const wrongCount = Object.values(quiz.answeredQuestions || {})
                  .filter(a => a && !a.result?.isCorrect).length;
                
                return (
                  <HistoryItem 
                    key={quiz.id}
                    quiz={quiz}
                    wrongCount={wrongCount}
                    onLoad={loadQuizFromHistory}
                    onDelete={deleteQuizFromHistory}
                    onRetryWrong={startWrongAnswersQuiz}
                    fetchDomainStats={fetchDomainStatsForQuiz}
                  />
                );
              })}
            </div>
          );
            })()}
        </div>
      ) : quizStarted && questions.length === 0 ? (
        <div className="loading">Loading questions...</div>
      ) : quizStarted && (
        <main className="question-container">
          {(() => {
            const currentQuestion = questions[currentQuestionIndex];
            if (!currentQuestion) {
              return <div className="loading">Loading question...</div>;
            }
            const isHotspotDropdown = currentQuestion.questionType === 'hotspot-dropdown';
            const isHotspot = currentQuestion.isHotspot && !isHotspotDropdown;
            const requiredCount = (isHotspot || isHotspotDropdown) ? 0 : getRequiredAnswerCount(currentQuestion);
            return (
              <>
                <div className="question">
                  <h2>Question {currentQuestion.id}</h2>
                  {!isHotspot && !isHotspotDropdown && requiredCount > 1 && (
                    <div className="multi-select-hint">
                      Select {requiredCount} answers ({selectedAnswers.length}/{requiredCount} selected)
                    </div>
                  )}
                  <p>{currentQuestion.question}</p>
                  {isHotspot && currentQuestion.image && (
                    <div className="hotspot-image-container">
                      <img 
                        src={currentQuestion.image} 
                        alt={`Question ${currentQuestion.id}`}
                        className="hotspot-image"
                      />
                    </div>
                  )}
                  {!isHotspot && !isHotspotDropdown && currentQuestion.imagePath && (
                    <div className="question-image-container">
                      <img
                        src={`/${currentQuestion.imagePath}`}
                        alt={`Question ${currentQuestion.id}`}
                        className="question-image"
                      />
                    </div>
                  )}
                </div>

                {/* ---- Hotspot dropdown (new interactive format) ---- */}
                {isHotspotDropdown && currentQuestion.hotspot && (
                  <HotspotQuestion
                    question={currentQuestion.question}
                    hotspot={currentQuestion.hotspot}
                    imagePath={currentQuestion.imagePath ? `/${currentQuestion.imagePath}` : null}
                    disabled={examActive}
                    onResult={(res) => {
                      const newScore = {
                        correct: score.correct + (res.isCorrect ? 1 : 0),
                        total: score.total + 1,
                      };
                      setScore(newScore);
                      setResult({ ...res, isHotspotDropdown: true });
                      setShowResult(true);
                      const newAnswered = {
                        ...answeredQuestions,
                        [currentQuestionIndex]: {
                          selectedAnswers: res.slotResults.map((r) => r.selected),
                          result: { isCorrect: res.isCorrect, correctAnswers: [], explanation: currentQuestion.explanation || '' },
                          timestamp: new Date().toISOString(),
                        },
                      };
                      setAnsweredQuestions(newAnswered);
                      autoSaveQuiz(currentQuestionIndex, newScore, newAnswered, false);
                    }}
                  />
                )}

                {isHotspotDropdown && showResult && (
                  <div className="result">
                    {currentQuestion.explanation && (
                      <div className="explanation">
                        <h3>Explanation:</h3>
                        <p>{currentQuestion.explanation}</p>
                      </div>
                    )}
                    <div className="navigation">
                      {reviewMode ? null : viewMode === 'quiz' ? (
                        <>
                          <button className="next-btn" onClick={nextQuestion}>
                            {currentQuestionIndex < questions.length - 1 ? 'Next Question' : 'Finish Quiz'}
                          </button>
                          {currentQuestionIndex >= questions.length - 1 && (
                            <button className="reset-btn" onClick={resetQuiz}>Start New Quiz</button>
                          )}
                        </>
                      ) : (
                        <button className="reset-btn" onClick={() => setShowResult(false)}>Continue Browsing</button>
                      )}
                    </div>
                  </div>
                )}

                {!isHotspot && !isHotspotDropdown && (
                  <div className="options">
                    {currentQuestion.options.map((option) => (
                      <div
                        key={option.letter}
                        className={(() => {
                          const reviewData = reviewMode ? answeredQuestions[currentQuestionIndex] : null;
                          const showFeedback = reviewMode ? true : showResult;
                          const feedbackResult = reviewMode ? reviewData?.result : result;
                          const feedbackSelected = reviewMode ? (reviewData?.selectedAnswers || []) : selectedAnswers;
                          return `option ${feedbackSelected.includes(option.letter) ? 'selected' : ''} ${
                            showFeedback && feedbackResult?.correctAnswers?.includes(option.letter) ? 'correct' : ''
                          } ${
                            showFeedback && feedbackSelected.includes(option.letter) && !feedbackResult?.correctAnswers?.includes(option.letter) ? 'incorrect' : ''
                          }`;
                        })()}
                        onClick={() => handleAnswerSelect(option.letter)}
                      >
                        <span className="option-letter">{option.letter}.</span>
                        <span className="option-text">{option.text}</span>
                      </div>
                    ))}
                  </div>
                )}

                {isHotspot && !showResult && (
                  <div className="hotspot-actions">
                    <button 
                      className="submit-btn"
                      onClick={() => {
                        setShowResult(true);
                        setResult({ isHotspot: true });
                      }}
                    >
                      Reveal Answer
                    </button>
                  </div>
                )}

                {isHotspot && showResult && currentQuestion.answerImage && (
                  <div className="result">
                    <div className="hotspot-answer-container">
                      <h3>Answer:</h3>
                      <img 
                        src={currentQuestion.answerImage} 
                        alt={`Answer for Question ${currentQuestion.id}`}
                        className="hotspot-image"
                      />
                    </div>
                    <div className="navigation">
                      {reviewMode ? null : viewMode === 'quiz' ? (
                        <>
                          <button className="next-btn" onClick={nextQuestion}>
                            {currentQuestionIndex < questions.length - 1 ? 'Next Question' : 'Finish Quiz'}
                          </button>
                          {currentQuestionIndex >= questions.length - 1 && (
                            <button className="reset-btn" onClick={resetQuiz}>
                              Start New Quiz
                            </button>
                          )}
                        </>
                      ) : (
                        <button className="reset-btn" onClick={() => setShowResult(false)}>
                          Continue Browsing
                        </button>
                      )}
                    </div>
                  </div>
                )}

                {(viewMode !== 'quiz' || reviewMode) && (
                  <div className="navigation-controls">
                    <button 
                      onClick={() => navigateQuestion('prev')} 
                      disabled={currentQuestionIndex === 0}
                      className="nav-btn"
                    >
                      ← Previous
                    </button>
                    <div className="question-jump">
                      <span className="question-counter">
                        {currentQuestionIndex + 1} / {questions.length}
                      </span>
                      <button 
                        className="jump-btn"
                        onClick={() => setShowJumpDialog(true)}
                      >
                        Jump to #
                      </button>
                    </div>
                    <button 
                      onClick={() => navigateQuestion('next')} 
                      disabled={currentQuestionIndex === questions.length - 1}
                      className="nav-btn"
                    >
                      Next →
                    </button>
                  </div>
                )}

                {!isHotspot && !isHotspotDropdown && !showResult && !reviewMode && (
                  <button 
                    className="submit-btn" 
                    onClick={submitAnswer} 
                    disabled={selectedAnswers.length !== requiredCount || loading}
                  >
                    {loading ? 'Checking...' : `Submit Answer${requiredCount > 1 ? 's' : ''}`}
                  </button>
                )}

                {!isHotspot && !isHotspotDropdown && (showResult || reviewMode) && (result || (reviewMode && answeredQuestions[currentQuestionIndex])) && (
                  <div className="result">
                    <div className={`result-status ${result.isCorrect ? 'correct' : 'incorrect'}`}>
                      {result.isCorrect ? '✅ Correct!' : '❌ Incorrect'}
                    </div>
                    
                    <div className="correct-answer">
                      <strong>Correct Answer{result.correctAnswers?.length > 1 ? 's' : ''}: {result.correctAnswers?.join(', ') || result.correctAnswer}</strong>
                    </div>

                    <div className="explanation">
                      <h3>Explanation:</h3>
                      <p>{result.explanation}</p>
                    </div>

                    {result.perOptionRationales && Object.keys(result.perOptionRationales).length > 0 && (
                      <div className="per-option-rationales">
                        <h3>Why each option is right or wrong:</h3>
                        <ul>
                          {(currentQuestion.options || [])
                            .filter((opt) => result.perOptionRationales[opt.letter])
                            .map((opt) => {
                              const isRight = (result.correctAnswers || []).includes(opt.letter);
                              return (
                                <li
                                  key={opt.letter}
                                  className={isRight ? 'rationale-correct' : 'rationale-incorrect'}
                                >
                                  <span className="rationale-letter">
                                    {isRight ? '\u2705' : '\u274c'} {opt.letter}
                                  </span>
                                  <span className="rationale-text">
                                    {result.perOptionRationales[opt.letter]}
                                  </span>
                                </li>
                              );
                            })}
                        </ul>
                      </div>
                    )}

                    {result.concepts && result.concepts.length > 0 && (
                      <div className="concepts">
                        <h3>Key Concepts:</h3>
                        <ul>
                          {result.concepts.map((concept, index) => (
                            <li key={index}>{concept}</li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {result.documentationUrl && (
                      <div className="documentation-link">
                        <a href={result.documentationUrl} target="_blank" rel="noopener noreferrer" className="aws-doc-btn">
                          📚 {result.documentationLabel || 'AWS Documentation'}
                        </a>
                      </div>
                    )}

                    <div className="navigation">
                      {reviewMode ? null : viewMode === 'quiz' ? (
                        <>
                          <button className="next-btn" onClick={nextQuestion}>
                            {currentQuestionIndex < questions.length - 1 ? 'Next Question' : 'Finish Quiz'}
                          </button>
                          {currentQuestionIndex >= questions.length - 1 && (
                            <button className="reset-btn" onClick={resetQuiz}>
                              Start New Quiz
                            </button>
                          )}
                        </>
                      ) : (
                        <button className="reset-btn" onClick={() => setShowResult(false)}>
                          Continue Browsing
                        </button>
                      )}
                    </div>
                  </div>
                )}
                {!isHotspot && !isHotspotDropdown && currentQuestion.options && currentQuestion.options.length > 0 && (
                  <VoteWidget
                    cert={selectedCertification?.id}
                    questionId={currentQuestion.id}
                    options={currentQuestion.options}
                  />
                )}
              </>
            );
          })()}
        </main>
      )}
        </>
      )}
    </div>
  );
}

export default App;